import { API_BASE } from '../config';
import { wrapScriptForExecution } from '../utils/scriptWrapper';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Terminal as TerminalIcon, Activity, HardDrive, Cpu, Microchip, Network, AlertTriangle, Battery, Thermometer, Camera, FolderGit2, FolderOpen, FileText, UploadCloud, ShieldCheck, ShieldAlert, Play, Square, Settings, AlertCircle, Mail, Download, Package, ShieldOff, Database, Ban, Home, Clock, BellOff, Bell } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import 'xterm/css/xterm.css';

interface DeviceStats {
  id: string;
  cpuUsage: number;
  ramTotal: number;
  ramUsed: number;
  diskTotal: number;
  diskUsed: number;
  diskDetails: string; // JSON array
  networkUp?: number;
  networkDown?: number;
  diskIoRead?: number;
  diskIoWrite?: number;
  timestamp: string;
  uptime?: number;
  processes?: string;
  cpuTemp?: number;
  battery?: string;
}

function formatBytes(bytes: number) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024, dm = 2, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export default function DeviceDetails() {
  const { id } = useParams();
  const [device, setDevice] = useState<any>(null);
  const [stats, setStats] = useState<DeviceStats[]>([]);
  const [diskHistory, setDiskHistory] = useState<any[]>([]);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [screenshotBase64s, setScreenshotBase64s] = useState<string[]>([]);
  const [screenshotStatus, setScreenshotStatus] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [dirList, setDirList] = useState<any[]>([]);
  const [fileStatus, setFileStatus] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'processes' | 'services' | 'startup' | 'events' | 'network' | 'firewall' | 'registry' | 'software' | 'updates' | 'terminal' | 'files' | 'screenshot' | 'scheduled-tasks' | 'settings'>('dashboard');
  const [silenceLoading, setSilenceLoading] = useState(false);
  const [customSilenceDate, setCustomSilenceDate] = useState('');

  const [currentStats, setCurrentStats] = useState<{
    cpuUsage: number; ramTotal: number; ramUsed: number;
    networkUp: number; networkDown: number;
    diskIoRead: number; diskIoWrite: number; cpuTemp: number;
  } | null>(null);
  const liveWsRef = useRef<WebSocket | null>(null);

  const isLinux = device?.platform === 'linux';

  const [latestAgentInfo, setLatestAgentInfo] = useState<{version: string, url: string} | null>(null);
  const [updateAgentLoading, setUpdateAgentLoading] = useState(false);

  const [processes, setProcesses] = useState<any[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processesError, setProcessesError] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [processSortConfig, setProcessSortConfig] = useState<{ key: string, direction: 'asc'|'desc' } | null>({ key: 'cpu', direction: 'desc' });

  const [startupApps, setStartupApps] = useState<any[]>([]);
  const [startupLoading, setStartupLoading] = useState(false);
  const [startupError, setStartupError] = useState('');
  const [startupFilter, setStartupFilter] = useState('');
  const [startupSortConfig, setStartupSortConfig] = useState<{ key: string, direction: 'asc'|'desc' } | null>({ key: 'name', direction: 'asc' });

  const [registryPath, setRegistryPath] = useState<string>('');
  const [registryData, setRegistryData] = useState<{Keys: any[], Values: any[]}>({ Keys: [], Values: [] });
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState('');
  const [services, setServices] = useState<any[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceError, setServiceError] = useState('');
  const [serviceActionLoading, setServiceActionLoading] = useState<string | null>(null);
  const [serviceFilter, setServiceFilter] = useState('');
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [updates, setUpdates] = useState<any[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [updatesError, setUpdatesError] = useState('');
  const [updateActionLoading, setUpdateActionLoading] = useState<boolean>(false);
  const [softwareList, setSoftwareList] = useState<any[]>([]);
  const [softwareLoading, setSoftwareLoading] = useState(false);
  const [softwareError, setSoftwareError] = useState('');
  const [softwareFilter, setSoftwareFilter] = useState('');
  const [uninstallLoading, setUninstallLoading] = useState<string | null>(null);
  const [connections, setConnections] = useState<any[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');
  const [connectionSortConfig, setConnectionSortConfig] = useState<{ key: string, direction: 'asc'|'desc' } | null>({ key: 'trusted_local', direction: 'desc' });

  const [firewallRules, setFirewallRules] = useState<any[]>([]);
  const [firewallLoading, setFirewallLoading] = useState(false);
  const [firewallError, setFirewallError] = useState('');
  const [firewallFilter, setFirewallFilter] = useState('');
  const [firewallSortConfig, setFirewallSortConfig] = useState<{ key: string, direction: 'asc'|'desc' } | null>({ key: 'DisplayName', direction: 'asc' });

  const [killLoading, setKillLoading] = useState<number | null>(null);
  const [blockIpLoading, setBlockIpLoading] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scheduled Tasks state
  const [scheduledTasks, setScheduledTasks] = useState<any[]>([]);
  const [scheduledTasksLoading, setScheduledTasksLoading] = useState(false);
  const [scheduledTasksError, setScheduledTasksError] = useState('');
  const [taskRuns, setTaskRuns] = useState<any[]>([]);
  const [taskRunsTotal, setTaskRunsTotal] = useState(0);
  const [taskRunsPage, setTaskRunsPage] = useState(1);
  const [taskRunsLoading, setTaskRunsLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [taskForm, setTaskForm] = useState({ name: '', command: '', cron: '0 3 * * 0', enabled: true });
  const [taskFormError, setTaskFormError] = useState('');
  const [taskFormSaving, setTaskFormSaving] = useState(false);
  const [runNowLoading, setRunNowLoading] = useState<string | null>(null);
  const [recentDeviceRuns, setRecentDeviceRuns] = useState<any[]>([]);

  // Table row virtualization — render limited rows by default, expand on demand
  const TABLE_ROW_LIMIT = 100;
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const toggleTableExpand = (table: string) => setExpandedTables(prev => {
    const next = new Set(prev);
    if (next.has(table)) next.delete(table); else next.add(table);
    return next;
  });
  const sliceRows = <T,>(rows: T[], table: string): T[] => expandedTables.has(table) ? rows : rows.slice(0, TABLE_ROW_LIMIT);

  // Script Library picker state
  const [libraryScripts, setLibraryScripts] = useState<any[]>([]);
  const [showScriptPicker, setShowScriptPicker] = useState<'terminal' | 'task' | null>(null);

  // Interactive terminal state
  const termContainerRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const termWsRef = useRef<WebSocket | null>(null);
  const termIdRef = useRef<string | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [termConnected, setTermConnected] = useState(false);
  const [termConnecting, setTermConnecting] = useState(false);
  const [termExited, setTermExited] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const signal = controller.signal;
      const [devRes, statsRes, diskRes] = await Promise.all([
        fetch(`${API_BASE}/api/devices/${id}`, { credentials: 'include' as const, signal }),
        fetch(`${API_BASE}/api/devices/${id}/stats`, { credentials: 'include' as const, signal }),
        fetch(`${API_BASE}/api/devices/${id}/disk-history`, { credentials: 'include' as const, signal }),
      ]);

      if (devRes.ok) setDevice(await devRes.json());
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.reverse());
      }
      if (diskRes.ok) {
        setDiskHistory(await diskRes.json());
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error(e);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    let interval = setInterval(fetchData, 30000);

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
        abortRef.current?.abort();
      } else {
        fetchData();
        interval = setInterval(fetchData, 30000);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [id, fetchData]);

  useEffect(() => {
    if (!device) return;
    const controller = new AbortController();
    const platform = device.platform || 'win32';
    fetch(`${API_BASE}/api/agents/latest?platform=${platform}`, { credentials: 'include', signal: controller.signal })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setLatestAgentInfo(data); })
      .catch(() => {});
    return () => controller.abort();
  }, [device?.platform]);

  const triggerAgentUpdate = async () => {
    if (!window.confirm("Trigger an automatic agent update? The device will briefly disconnect and deploy the latest executable.")) return;
    setUpdateAgentLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/update-check`, {
        method: 'POST',
        credentials: 'include' as const
      });
      if (res.ok) {
        alert("Update signal dispatched!");
      }
    } catch(e) {
      alert("Failed to send update check.");
    } finally {
      setUpdateAgentLoading(false);
    }
  };


  const executeReboot = async () => {
    if (!window.confirm('Are you sure you want to reboot this device?')) return;
    try {
      await fetch(`${API_BASE}/api/devices/${id}/reboot`, {
        method: 'POST',
        credentials: 'include' as const
      });
      alert('Reboot command sent.');
    } catch (e) {
      alert('Failed to send reboot command.');
    }
  };

  const sendTestAlert = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/test-alert`, {
        method: 'POST',
        credentials: 'include' as const
      });
      if (res.ok) {
        alert('Test alert dispatched! Check your email inbox or backend console.');
      } else {
        alert('Failed to send test alert.');
      }
    } catch (e) {
      alert('Network error sending test alert.');
    }
  };

  const requestScreenshot = async () => {
    setScreenshotStatus('Capturing screenshot (this may take a moment)...');
    setScreenshotBase64(null);
    setScreenshotBase64s([]);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/screenshot`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && (data.imageBase64 || data.imageBase64s)) {
        if (data.imageBase64s) setScreenshotBase64s(data.imageBase64s);
        if (data.imageBase64) setScreenshotBase64(data.imageBase64);
        setScreenshotStatus('Screenshots captured successfully.');
      } else {
        setScreenshotStatus(`Server Error: ${data.error || 'Failed to capture.'}`);
      }
    } catch (err) {
      setScreenshotStatus('Network Error requesting screenshot.');
    }
  };

  const fetchDirList = async (targetPath: string) => {
    setFileStatus(`Loading directory: ${targetPath}...`);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ path: targetPath })
      });
      const data = await res.json();
      if (res.ok && data.list) {
        setDirList(data.list);
        setCurrentPath(data.currentPath);
        setFileStatus('');
      } else {
        setFileStatus(`Error: ${data.error || 'Failed to list directory'}`);
      }
    } catch (e) {
      setFileStatus('Network error.');
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    if (!window.confirm(`Download ${fileName} over secure tunnel?`)) return;
    setFileStatus(`Downloading ${fileName}...`);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ path: filePath })
      });
      const data = await res.json();
      if (res.ok && data.base64) {
        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${data.base64}`;
        link.download = data.fileName || fileName;
        link.click();
        setFileStatus(`Downloaded ${fileName}`);
      } else {
        setFileStatus(`Download failed: ${data.error}`);
      }
    } catch(e) {
      setFileStatus('Network error during download.');
    }
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if path is valid directory
    if (!currentPath || currentPath === '') {
      setFileStatus(`Please navigate to a valid folder first.`);
      return;
    }

    setFileStatus(`Reading ${file.name} for upload...`);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const result = event.target?.result as string;
      // Extract base64 part
      const base64 = result.split(',')[1];
      if (!base64) {
         setFileStatus(`Failed to parse file buffer.`);
         return;
      }

      setFileStatus(`Uploading ${file.name} (this may take a moment based on size)...`);
      const sep = currentPath.includes('\\') ? '\\' : '/';
      const targetPath = currentPath.endsWith(sep) ? currentPath + file.name : currentPath + sep + file.name;

      try {
        const res = await fetch(`${API_BASE}/api/devices/${id}/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
          body: JSON.stringify({ path: targetPath, base64 })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setFileStatus(`Successfully uploaded ${file.name}`);
          fetchDirList(currentPath); // Refresh the view
        } else {
          setFileStatus(`Upload failed: ${data.error}`);
        }
      } catch (err) {
        setFileStatus('Network error during upload.');
      }
    };
    reader.readAsDataURL(file);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fetchProcesses = async () => {
    setProcessesLoading(true);
    setProcessesError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/processes`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setProcesses(data);
      } else {
        setProcessesError(data.error || 'Failed to fetch processes');
      }
    } catch(e) {
      setProcessesError('Network error fetching processes.');
    } finally {
      setProcessesLoading(false);
    }
  };

  const fetchStartupApps = async () => {
    setStartupLoading(true);
    setStartupError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/startup`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setStartupApps(data);
      } else {
        setStartupError(data.error || 'Failed to fetch startup apps');
      }
    } catch(e) {
      setStartupError('Network error fetching startup apps.');
    } finally {
      setStartupLoading(false);
    }
  };

  const deleteStartupApp = async (name: string, location: string) => {
    if (!window.confirm(`Are you sure you want to delete the autorun entry for ${name}? This will prevent it from starting automatically on boot.`)) return;
    setStartupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/startup/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ name, location })
      });
      const data = await res.json();
      if (res.ok) {
        fetchStartupApps();
      } else {
        alert(`Delete Error: ${data.error || 'Failed to delete.'}`);
      }
    } catch(e) {
      alert('Network error during deletion.');
    } finally {
      if (activeTab === 'startup') setStartupLoading(false);
    }
  };

  const fetchServices = async () => {
    setServicesLoading(true);
    setServiceError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/services`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && data.services) {
        // Sort services alphabetically
        const sorted = data.services.sort((a: any, b: any) => a.name.localeCompare(b.name));
        setServices(sorted);
      } else {
        setServiceError(data.error || 'Failed to fetch services');
      }
    } catch(e) {
      setServiceError('Network error fetching services.');
    } finally {
      setServicesLoading(false);
    }
  };

  const handleServiceAction = async (serviceName: string, action: 'start' | 'stop' | 'restart') => {
    setServiceActionLoading(serviceName);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/services/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ serviceName, action })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert(`Successfully sent ${action} command to ${serviceName}.`);
        fetchServices();
      } else {
        alert(`Error: ${data.error || 'Access Denied (Try running agent as Administrator)'}`);
      }
    } catch(e) {
      alert('Network error communicating with agent.');
    } finally {
      setServiceActionLoading(null);
    }
  };

  const fetchEvents = async () => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/events`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && data.events) {
        setEvents(data.events);
      } else {
        setEventsError(data.error || 'Failed to fetch event logs');
      }
    } catch(e) {
      setEventsError('Network error fetching events.');
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchUpdates = async () => {
    setUpdatesLoading(true);
    setUpdatesError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/updates/check`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && data.updates) {
        setUpdates(data.updates);
      } else {
        setUpdatesError(data.error || 'Failed to search updates (COM object timeouts are common on old disks).');
      }
    } catch(e) {
      setUpdatesError('Network error checking updates.');
    } finally {
      setUpdatesLoading(false);
    }
  };

  const installUpdates = async () => {
    if (!window.confirm("Are you sure you want to install pending background updates silently? This bypasses user prompts and forces OS downloads which can take 10+ minutes.")) return;
    setUpdateActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/updates/install`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok) {
        alert('Updates Installed Successfully! Reboot might be required.');
        fetchUpdates();
      } else {
        alert(`Update Error: ${data.error || 'Check event viewer.'}`);
      }
    } catch(e) {
      alert('Network error during install.');
    } finally {
      setUpdateActionLoading(false);
    }
  };

  const fetchSoftware = async () => {
    setSoftwareLoading(true);
    setSoftwareError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/software`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && data.software) {
        setSoftwareList(data.software);
      } else {
        setSoftwareError(data.error || 'Failed to fetch software list.');
      }
    } catch(e) {
      setSoftwareError('Network error checking installed software.');
    } finally {
      setSoftwareLoading(false);
    }
  };

  const executeUninstall = async (name: string, uninstallString: string) => {
    if (!window.confirm(`Are you absolutely sure you want to attempt a silent remote uninstallation of ${name}?`)) return;
    setUninstallLoading(name);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/software/uninstall`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ uninstallString })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`${name} remote uninstallation sequence initiated cleanly!`);
        fetchSoftware();
      } else {
        alert(`Uninstall Error: ${data.error || 'Check standard output.'}`);
      }
    } catch(e) {
      alert('Network error during uninstallation.');
    } finally {
      setUninstallLoading(null);
    }
  };

  const fetchConnections = async () => {
    setConnectionsLoading(true);
    setConnectionsError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/network`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && data.connections) {
        setConnections(data.connections);
      } else {
        setConnectionsError(data.error || 'Failed to fetch network connections.');
      }
    } catch(e) {
      setConnectionsError('Network error checking connections.');
    } finally {
      setConnectionsLoading(false);
    }
  };

  const fetchFirewallRules = async () => {
    setFirewallLoading(true);
    setFirewallError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/firewall`, {
        method: 'POST',
        credentials: 'include' as const
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setFirewallRules(data);
      } else {
        setFirewallError(data.error || 'Failed to fetch firewall rules');
      }
    } catch(e) {
      setFirewallError('Network error fetching firewall rules.');
    } finally {
      setFirewallLoading(false);
    }
  };

  const fetchRegistry = async (path: string = '') => {
    if (!path || path.trim() === '') {
      setRegistryPath('');
      if (!isLinux) {
        setRegistryData({
          Keys: [
            { Name: 'HKLM:' },
            { Name: 'HKCU:' },
            { Name: 'HKCR:' },
            { Name: 'HKU:' },
            { Name: 'HKCC:' }
          ],
          Values: []
        });
        return;
      }
      // Linux: fall through to fetch /etc listing from agent
    }

    setRegistryLoading(true);
    setRegistryError('');
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/registry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ path })
      });
      const data = await res.json();
      if (res.ok && data.result) {
        setRegistryData(data.result);
        setRegistryPath(path);
      } else {
        setRegistryError(data.error || 'Failed to fetch registry path.');
      }
    } catch(e) {
      setRegistryError('Network error checking registry.');
    } finally {
      setRegistryLoading(false);
    }
  };

  const executeKill = async (pid: number, name: string) => {
    if (!window.confirm(`Are you absolutely sure you want to physically kill ${name} (PID: ${pid})? This cleanly terminates the payload out of memory instantly.`)) return;
    setKillLoading(pid);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/kill-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ pid })
      });
      const data = await res.json();
      if (res.ok) {
        if (activeTab === 'network') fetchConnections();
        if (activeTab === 'processes') fetchProcesses();
      } else {
        alert(`Kill Error: ${data.error || 'Process may have already closed.'}`);
      }
    } catch(e) {
      alert('Network error during termination.');
    } finally {
      setKillLoading(null);
    }
  };



  const blockIp = async (ip: string) => {
    if (!window.confirm(isLinux
      ? `Block ${ip} using ufw? This will add deny rules for this IP.`
      : `Block ${ip} in Windows Firewall? This will create an outbound+inbound deny rule for this IP.`
    )) return;
    setBlockIpLoading(ip);
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/block-ip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ ip })
      });
      const data = await res.json();
      if (res.ok) {
        fetchConnections();
      } else {
        alert(`Block IP Error: ${data.error || 'Failed to create firewall rule.'}`);
      }
    } catch(e) {
      alert('Network error while blocking IP.');
    } finally {
      setBlockIpLoading(null);
    }
  };

  const fetchScheduledTasks = async () => {
    setScheduledTasksLoading(true);
    setScheduledTasksError('');
    try {
      const res = await fetch(`${API_BASE}/api/scheduled-tasks?deviceId=${id}`, {
        credentials: 'include' as const,
      });
      const data = await res.json();
      if (res.ok) {
        setScheduledTasks(data);
      } else {
        setScheduledTasksError(data.error || 'Failed to load scheduled tasks');
      }
    } catch { setScheduledTasksError('Failed to load scheduled tasks'); }
    setScheduledTasksLoading(false);
  };

  const fetchTaskRuns = async (taskId: string, page = 1) => {
    setTaskRunsLoading(true);
    setSelectedTaskId(taskId);
    setTaskRunsPage(page);
    try {
      const res = await fetch(`${API_BASE}/api/scheduled-tasks/${taskId}/runs?page=${page}&pageSize=20`, {
        credentials: 'include' as const,
      });
      const data = await res.json();
      if (res.ok) {
        setTaskRuns(data.runs);
        setTaskRunsTotal(data.total);
      }
    } catch {}
    setTaskRunsLoading(false);
  };

  const fetchRecentDeviceRuns = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/devices/${id}/task-runs?pageSize=10`, {
        credentials: 'include' as const,
      });
      const data = await res.json();
      if (res.ok) setRecentDeviceRuns(data.runs);
    } catch {}
  };

  const saveTask = async () => {
    setTaskFormSaving(true);
    setTaskFormError('');
    try {
      const url = editingTask
        ? `${API_BASE}/api/scheduled-tasks/${editingTask.id}`
        : `${API_BASE}/api/scheduled-tasks`;
      const method = editingTask ? 'PUT' : 'POST';
      const body = editingTask
        ? { name: taskForm.name, command: taskForm.command, cron: taskForm.cron, enabled: taskForm.enabled }
        : { ...taskForm, deviceId: id };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setShowTaskForm(false);
        setEditingTask(null);
        setTaskForm({ name: '', command: '', cron: '0 3 * * 0', enabled: true });
        fetchScheduledTasks();
      } else {
        setTaskFormError(data.error || 'Failed to save task');
      }
    } catch { setTaskFormError('Failed to save task'); }
    setTaskFormSaving(false);
  };

  const deleteTask = async (taskId: string) => {
    if (!confirm('Delete this scheduled task?')) return;
    try {
      await fetch(`${API_BASE}/api/scheduled-tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include' as const,
      });
      fetchScheduledTasks();
      if (selectedTaskId === taskId) { setSelectedTaskId(null); setTaskRuns([]); }
    } catch {}
  };

  const runTaskNow = async (taskId: string) => {
    setRunNowLoading(taskId);
    try {
      const res = await fetch(`${API_BASE}/api/scheduled-tasks/${taskId}/run`, {
        method: 'POST',
        credentials: 'include' as const,
      });
      const data = await res.json();
      if (!res.ok) setScheduledTasksError(data.error || 'Failed to run task');
      setTimeout(() => {
        fetchScheduledTasks();
        if (selectedTaskId === taskId) fetchTaskRuns(taskId);
        fetchRecentDeviceRuns();
      }, 2000);
    } catch {}
    setRunNowLoading(null);
  };

  const toggleTaskEnabled = async (task: any) => {
    try {
      await fetch(`${API_BASE}/api/scheduled-tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include' as const,
        body: JSON.stringify({ enabled: !task.enabled }),
      });
      fetchScheduledTasks();
    } catch {}
  };

  const fetchLibraryScripts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/scripts`, {
        credentials: 'include' as const,
      });
      const data = await res.json();
      if (res.ok) setLibraryScripts(data);
    } catch {}
  };

  const ScriptPickerDropdown = ({ target }: { target: 'terminal' | 'task' }) => {
    if (showScriptPicker !== target) return null;
    return (
      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: 'var(--bg-elevated)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '0.5rem', minWidth: '300px', maxHeight: '300px', overflowY: 'auto', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.8)' }}>
        {libraryScripts.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', padding: '0.5rem', margin: 0, fontSize: '0.85rem' }}>No scripts saved yet</p>
        ) : libraryScripts.map((s: any) => (
          <button key={s.id} onClick={() => {
            const wrapped = wrapScriptForExecution(s.type, s.content);
            if (target === 'terminal' && termConnected && termWsRef.current?.readyState === WebSocket.OPEN) {
              // Paste into live PTY session
              termWsRef.current.send(JSON.stringify({
                type: 'terminal_input',
                payload: { termId: termIdRef.current, data: wrapped }
              }));
              xtermRef.current?.focus();
            } else {
              setTaskForm(prev => ({ ...prev, command: wrapped }));
            }
            setShowScriptPicker(null);
          }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '0.5rem', cursor: 'pointer', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '0.85rem' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>{s.name}</span>
              <span style={{ background: s.type === 'cmd' ? 'var(--accent-primary)' : s.type === 'bat' ? 'var(--warning)' : '#8b5cf6', color: '#fff', padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem' }}>{s.type}</span>
            </div>
            {s.description && <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '2px' }}>{s.description}</div>}
          </button>
        ))}
      </div>
    );
  };

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getTcpState = (state: any) => {
    const map: Record<string, string> = {
      '1': 'Closed', '2': 'Listen', '3': 'SynSent', '4': 'SynReceived', 
      '5': 'Established', '6': 'FinWait1', '7': 'FinWait2', '8': 'CloseWait', 
      '9': 'Closing', '10': 'LastAck', '11': 'TimeWait', '12': 'DeleteTcb'
    };
    return map[String(state)] || String(state);
  };

  const isTrustedOrg = (org: string, hostnames: string[]) => {
    if (!org && (!hostnames || hostnames.length === 0)) return false;
    const safeTerms = ['google', 'microsoft', 'valve', 'amazon', 'aws', 'akamai', 'cloudflare', 'fastly', 'discord', 'apple', 'twitter', 'facebook', 'meta', 'github'];
    const safeMatch = safeTerms.some(term => 
      (org && org.toLowerCase().includes(term)) || 
      (hostnames && hostnames.some(h => h.toLowerCase().includes(term)))
    );
    return safeMatch;
  };

  const isLocalIp = (ip: string) => {
    if (!ip) return false;
    return ip.startsWith('192.168.') || ip.startsWith('10.') || ip.match(/^172\.(1[6-9]|2\d|3[01])\./) || ip.startsWith('127.') || ip.startsWith('169.254.');
  };

  const sortedServices = React.useMemo(() => {
    let sortableItems = [...services];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'running') {
           aVal = a.running ? 1 : 0;
           bVal = b.running ? 1 : 0;
        } else {
           if (aVal === null || aVal === undefined) aVal = '';
           if (bVal === null || bVal === undefined) bVal = '';
           aVal = String(aVal).toLowerCase();
           bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [services, sortConfig]);

  const requestConnectionSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (connectionSortConfig && connectionSortConfig.key === key && connectionSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setConnectionSortConfig({ key, direction });
  };

  const sortedAndFilteredConnections = React.useMemo(() => {
    let items = [...connections];

    if (connectionFilter) {
      const q = connectionFilter.toLowerCase();
      items = items.filter(c => {
        const isTrusted = isTrustedOrg(c.Geo?.organization_name, c.Hostnames);
        const localIp = isLocalIp(c.RemoteAddress);
        const trustStr = isTrusted ? 'trusted' : localIp ? 'local' : 'public';
        return (c.ProcessName || '').toLowerCase().includes(q) ||
               (c.RemoteAddress || '').toLowerCase().includes(q) ||
               trustStr.includes(q) ||
               (c.Geo?.organization_name || '').toLowerCase().includes(q);
      });
    }

    if (connectionSortConfig) {
      items.sort((a, b) => {
        let aVal = a[connectionSortConfig.key] || '';
        let bVal = b[connectionSortConfig.key] || '';

        if (connectionSortConfig.key === 'trusted_local') {
           aVal = isTrustedOrg(a.Geo?.organization_name, a.Hostnames) ? 2 : isLocalIp(a.RemoteAddress) ? 1 : 0;
           bVal = isTrustedOrg(b.Geo?.organization_name, b.Hostnames) ? 2 : isLocalIp(b.RemoteAddress) ? 1 : 0;
        } else {
           aVal = String(aVal).toLowerCase();
           bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) return connectionSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return connectionSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [connections, connectionFilter, connectionSortConfig]);

  const requestFirewallSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (firewallSortConfig && firewallSortConfig.key === key && firewallSortConfig.direction === 'asc') direction = 'desc';
    setFirewallSortConfig({ key, direction });
  };

  const sortedAndFilteredFirewallRules = React.useMemo(() => {
    let items = [...firewallRules];
    if (firewallFilter) {
      const q = firewallFilter.toLowerCase();
      items = items.filter(r => String(r.DisplayName || '').toLowerCase().includes(q) || String(r.Description || '').toLowerCase().includes(q) || String(r.Action || '').toLowerCase().includes(q));
    }
    if (firewallSortConfig) {
      items.sort((a, b) => {
        let aVal = String(a[firewallSortConfig.key] || '').toLowerCase();
        let bVal = String(b[firewallSortConfig.key] || '').toLowerCase();
        
        // Handle sorting for integer types like Profile (Domain=1, Private=2, Public=4)
        if (firewallSortConfig.key === 'Profile') {
          aVal = String(a.Profile);
          bVal = String(b.Profile);
        }
        if (firewallSortConfig.key === 'Action' && aVal === '4') aVal = 'allow';
        if (firewallSortConfig.key === 'Action' && bVal === '4') bVal = 'allow';

        if (aVal < bVal) return firewallSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return firewallSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [firewallRules, firewallFilter, firewallSortConfig]);

  const requestProcessSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (processSortConfig && processSortConfig.key === key && processSortConfig.direction === 'asc') direction = 'desc';
    setProcessSortConfig({ key, direction });
  };

  const sortedAndFilteredProcesses = React.useMemo(() => {
    let items = [...processes];
    if (processFilter) {
      const q = processFilter.toLowerCase();
      items = items.filter(p => p.name.toLowerCase().includes(q) || (p.command && p.command.toLowerCase().includes(q)) || String(p.pid).includes(q));
    }
    if (processSortConfig) {
      items.sort((a, b) => {
        let aVal = a[processSortConfig.key];
        let bVal = b[processSortConfig.key];
        if (processSortConfig.key === 'name' || processSortConfig.key === 'command') {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        } else {
          aVal = Number(aVal || 0);
          bVal = Number(bVal || 0);
        }
        if (aVal < bVal) return processSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return processSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [processes, processFilter, processSortConfig]);

  const requestStartupSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (startupSortConfig && startupSortConfig.key === key && startupSortConfig.direction === 'asc') direction = 'desc';
    setStartupSortConfig({ key, direction });
  };

  const sortedAndFilteredStartupApps = React.useMemo(() => {
    let items = [...startupApps];
    if (startupFilter) {
      const q = startupFilter.toLowerCase();
      items = items.filter(a => (a.Name || '').toLowerCase().includes(q) || (a.Command || '').toLowerCase().includes(q) || (a.Location || '').toLowerCase().includes(q) || (a.User || '').toLowerCase().includes(q));
    }
    if (startupSortConfig) {
      items.sort((a, b) => {
        let aVal = String(a[startupSortConfig.key] || '').toLowerCase();
        let bVal = String(b[startupSortConfig.key] || '').toLowerCase();
        if (aVal < bVal) return startupSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return startupSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [startupApps, startupFilter, startupSortConfig]);

  const filteredServices = sortedServices.filter(s => s.name.toLowerCase().includes(serviceFilter.toLowerCase()));

  const chartData = useMemo(() => {
    const POLL_INTERVAL = 30_000;
    const GAP_THRESHOLD = 90_000;
    const result: any[] = [];
    for (let i = 0; i < stats.length; i++) {
      const s = stats[i];
      if (i > 0) {
        const prev = new Date(stats[i - 1].timestamp).getTime();
        const curr = new Date(s.timestamp).getTime();
        const gap = curr - prev;
        if (gap > GAP_THRESHOLD) {
          // Cap null points so large gaps don't bloat the array
          const rawCount = Math.round(gap / POLL_INTERVAL) - 1;
          const nullPoints = Math.min(rawCount, 60);
          const step = gap / (nullPoints + 1);
          for (let n = 0; n < nullPoints; n++) {
            const t = new Date(prev + (n + 1) * step);
            result.push({ time: t.toLocaleTimeString(), cpu: null, ram: null, temp: null, upMbps: null, downMbps: null, readMBps: null, writeMBps: null });
          }
        }
      }
      result.push({
        time: new Date(s.timestamp).toLocaleTimeString(),
        cpu: Math.round(s.cpuUsage),
        ram: Math.round((s.ramUsed / s.ramTotal) * 100) || 0,
        temp: s.cpuTemp || 0,
        upMbps: s.networkUp ? parseFloat(((s.networkUp * 8) / 1000000).toFixed(2)) : 0,
        downMbps: s.networkDown ? parseFloat(((s.networkDown * 8) / 1000000).toFixed(2)) : 0,
        readMBps: s.diskIoRead ? parseFloat((s.diskIoRead / 1048576).toFixed(2)) : 0,
        writeMBps: s.diskIoWrite ? parseFloat((s.diskIoWrite / 1048576).toFixed(2)) : 0
      });
    }
    return result;
  }, [stats]);

  // Live stats WebSocket — connects on mount, sends watch_device, receives live_stats
  useEffect(() => {
    if (!id) return;

    const wsUrl = API_BASE.replace(/^http/, 'ws') + '/ws/frontend';
    const socket = new WebSocket(wsUrl);
    liveWsRef.current = socket;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'watch_device', payload: { deviceId: id } }));
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'live_stats') {
        setCurrentStats(msg.payload);
      }
    };

    socket.onclose = () => {
      liveWsRef.current = null;
    };

    return () => {
      socket.close();
      liveWsRef.current = null;
    };
  }, [id]);

  const latestStat = stats[stats.length - 1];
  const live = currentStats;
  // Live stats override latestStat for real-time gauges (when available)
  const displayStat = live ? {
    ...latestStat,
    cpuUsage: live.cpuUsage,
    ramTotal: live.ramTotal,
    ramUsed: live.ramUsed,
    networkUp: live.networkUp,
    networkDown: live.networkDown,
    diskIoRead: live.diskIoRead,
    diskIoWrite: live.diskIoWrite,
    cpuTemp: live.cpuTemp,
  } : latestStat;
  const hw = useMemo(() => device?.hardware ? JSON.parse(device.hardware) : null, [device?.hardware]);
  const liveDisks = useMemo(() => latestStat?.diskDetails ? JSON.parse(latestStat.diskDetails) : [], [latestStat?.diskDetails]);

  const diskChartData = useMemo(() => diskHistory.map(s => {
    const point: any = {
      time: new Date(s.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    };
    if (s.diskDetails) {
      try {
        const details = JSON.parse(s.diskDetails);
        details.forEach((d: any) => {
          point[d.fs] = d.size > 0 ? Math.round((d.used / d.size) * 100) : 0;
        });
      } catch (e) {}
    } else {
      point['Overall'] = s.diskTotal > 0 ? Math.round((s.diskUsed / s.diskTotal) * 100) : 0;
    }
    return point;
  }), [diskHistory]);

  useEffect(() => {
    if (device && currentPath === '') {
      fetchDirList(device.os.toLowerCase().includes('windows') ? 'C:\\' : '/');
    }
  }, [device]);

  // Interactive terminal connect/disconnect
  const connectTerminal = useCallback(() => {
    if (!id || termConnecting || termConnected) return;
    setTermConnecting(true);
    setTermExited(false);

    const wsUrl = API_BASE.replace(/^http/, 'ws') + '/ws/frontend';
    const socket = new WebSocket(wsUrl);
    termWsRef.current = socket;

    // Closure-local flag — avoids stale termConnected capture in the timeout below
    let started = false;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'watch_device', payload: { deviceId: id } }));

      if (termContainerRef.current) {
        if (xtermRef.current) {
          xtermRef.current.dispose();
        }
        termContainerRef.current.innerHTML = '';

        const term = new Terminal({
          cursorBlink: true,
          theme: { background: '#0f172a', foreground: '#e2e8f0', cursor: '#e2e8f0' },
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: 14,
        });
        const fitAddon = new FitAddon();
        term.loadAddon(fitAddon);
        term.open(termContainerRef.current);
        fitAddon.fit();

        xtermRef.current = term;
        fitAddonRef.current = fitAddon;

        const termId = crypto.randomUUID();
        termIdRef.current = termId;

        socket.send(JSON.stringify({
          type: 'terminal_start',
          payload: { termId, cols: term.cols, rows: term.rows }
        }));

        // Timeout if terminal_started never arrives (agent offline, etc.)
        setTimeout(() => {
          if (termWsRef.current && !started) {
            setTermConnecting(false);
            setTermExited(true);
            xtermRef.current?.write('\r\n\x1b[31m--- Connection timed out ---\x1b[0m\r\n');
          }
        }, 10000);

        term.onData((data) => {
          if (termWsRef.current?.readyState === WebSocket.OPEN) {
            termWsRef.current.send(JSON.stringify({
              type: 'terminal_input',
              payload: { termId: termIdRef.current, data }
            }));
          }
        });

        resizeObserverRef.current?.disconnect();
        const observer = new ResizeObserver(() => {
          if (fitAddonRef.current && xtermRef.current) {
            fitAddonRef.current.fit();
            if (termWsRef.current?.readyState === WebSocket.OPEN) {
              termWsRef.current.send(JSON.stringify({
                type: 'terminal_resize',
                payload: { termId: termIdRef.current, cols: xtermRef.current.cols, rows: xtermRef.current.rows }
              }));
            }
          }
        });
        resizeObserverRef.current = observer;
        if (termContainerRef.current) {
          observer.observe(termContainerRef.current);
        }
      }
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'terminal_started') {
        started = true;
        setTermConnected(true);
        setTermConnecting(false);
      } else if (msg.type === 'terminal_output') {
        xtermRef.current?.write(msg.payload.data);
      } else if (msg.type === 'terminal_exited') {
        setTermConnected(false);
        setTermConnecting(false);
        setTermExited(true);
        xtermRef.current?.write('\r\n\x1b[33m--- Session ended ---\x1b[0m\r\n');
      }
    };

    socket.onclose = () => {
      setTermConnected(false);
      setTermConnecting(false);
    };

    socket.onerror = () => {
      setTermConnected(false);
      setTermConnecting(false);
    };
  }, [id, termConnecting, termConnected]);

  const disconnectTerminal = useCallback(() => {
    if (termWsRef.current && termIdRef.current) {
      if (termWsRef.current.readyState === WebSocket.OPEN) {
        termWsRef.current.send(JSON.stringify({
          type: 'terminal_stop',
          payload: { termId: termIdRef.current }
        }));
      }
      termWsRef.current.close();
    }
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    termWsRef.current = null;
    termIdRef.current = null;
    setTermConnected(false);
    setTermConnecting(false);
  }, []);

  // Cleanup terminal on unmount
  useEffect(() => {
    return () => {
      disconnectTerminal();
      if (xtermRef.current) {
        xtermRef.current.dispose();
        xtermRef.current = null;
      }
    };
  }, [disconnectTerminal]);

  // Disconnect terminal when switching away from terminal tab
  useEffect(() => {
    if (activeTab !== 'terminal') {
      disconnectTerminal();
      if (xtermRef.current) {
        xtermRef.current.dispose();
        xtermRef.current = null;
      }
    }
  }, [activeTab, disconnectTerminal]);

  if (!device) return <div className="flex-center" style={{ height: '50vh' }}>Loading...</div>;

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
        <ArrowLeft size={16} /> Back to Dashboard
      </Link>

      {/* Header Panel */}
      <div className="glass-panel" style={{ padding: '2rem', marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <h2 style={{ fontSize: '1.75rem', margin: 0 }}>{device.nickname || device.hostname}</h2>
            {device.nickname && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{device.hostname}</span>
            )}
            <span className={`status-indicator status-${device.status}`} title={device.status} />
            {device.silencedUntil && new Date(device.silencedUntil) > new Date() && (
              <span style={{ background: 'rgba(234, 179, 8, 0.15)', color: '#eab308', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <BellOff size={14} /> {new Date(device.silencedUntil).getFullYear() >= 9999 ? 'Silenced indefinitely' : `Silenced until ${new Date(device.silencedUntil).toLocaleString()}`}
              </span>
            )}
            {device.agentVersion && latestAgentInfo && device.agentVersion !== latestAgentInfo.version && (
              <span style={{ background: 'var(--warning)', color: '#000', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FolderOpen size={14} /> Update Available (v{latestAgentInfo.version})
              </span>
            )}
            {device.agentVersion && (
              <span style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--text-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                Agent v{device.agentVersion}
              </span>
            )}
          </div>
          {(() => { try { const t = JSON.parse(device.tags || '[]'); return t.length > 0 ? (
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {t.map((tag: string) => (
                <span key={tag} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '10px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
          ) : null; } catch { return null; } })()}
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <p style={{ margin: 0 }}><strong>OS:</strong> {device.os} ({device.arch})</p>
            <p style={{ margin: 0 }}><strong>Uptime:</strong> {latestStat?.uptime ? `${Math.floor(latestStat.uptime / 86400)}d ${Math.floor((latestStat.uptime % 86400) / 3600)}h ${Math.floor((latestStat.uptime % 3600) / 60)}m` : 'Unknown'}</p>
            <p style={{ margin: 0 }}><strong>Local IP:</strong> {device.localIp || 'N/A'} <span style={{ margin: '0 8px' }}>|</span> <strong>Public IP:</strong> {device.publicIp || 'N/A'}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.8 }}>MAC: {device.macAddress}</p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {device.agentVersion && latestAgentInfo && device.agentVersion !== latestAgentInfo.version && (
            <button className="btn" style={{ background: 'var(--warning)', color: '#000', border: 'none' }} onClick={triggerAgentUpdate} disabled={updateAgentLoading}>
              <RefreshCw size={16} className={updateAgentLoading ? 'spin' : ''} /> Update Agent
            </button>
          )}
          <button className="btn btn-secondary" onClick={sendTestAlert}>
            <Mail size={16} /> Test Alert
          </button>
          <button className="btn btn-secondary" onClick={executeReboot}>
            <RefreshCw size={16} /> Reboot Device
          </button>
        </div>
      </div>

      {/* Primary Global Navigation Tabs */}
      <style>{`
        .nav-dropdown-container { position: relative; display: flex; }
        .nav-dropdown-menu { position: absolute; top: calc(100% - 4px); left: 0; display: none; flex-direction: column; background: var(--bg-elevated); border: 1px solid rgba(255,255,255,0.2); border-radius: 8px; padding: 0.5rem; min-width: 200px; z-index: 50; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.8); gap: 0.25rem; }
        .nav-dropdown-container:hover .nav-dropdown-menu { display: flex; }
        .nav-dropdown-menu button:hover { background: rgba(255,255,255,0.05) !important; color: white !important; }
      `}</style>
      <div className="glass-panel" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '2rem', padding: '0.5rem', borderRadius: '12px', alignItems: 'center', position: 'relative', zIndex: 100 }}>
        <button 
          onClick={() => setActiveTab('dashboard')}
          style={{ 
            background: activeTab === 'dashboard' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
            color: activeTab === 'dashboard' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <Activity size={18} color={activeTab === 'dashboard' ? '#0ea5e9' : 'currentColor'} /> Dashboard
        </button>
        <div className="nav-dropdown-container">
          <button 
            style={{ 
              background: (activeTab === 'processes' || activeTab === 'services' || activeTab === 'startup') ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
              color: (activeTab === 'processes' || activeTab === 'services' || activeTab === 'startup') ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
            }}
          >
            <Microchip size={18} color={(activeTab === 'processes' || activeTab === 'services' || activeTab === 'startup') ? '#22c55e' : 'currentColor'} /> System & Services ▼
          </button>
          <div className="nav-dropdown-menu">
            <button 
              onClick={() => { setActiveTab('processes'); if(processes.length === 0) fetchProcesses(); }}
              style={{ background: activeTab === 'processes' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'processes' ? '#22c55e' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Microchip size={16} /> Live Processes
            </button>
            <button 
              onClick={() => { setActiveTab('services'); if(services.length === 0) fetchServices(); }}
              style={{ background: activeTab === 'services' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'services' ? '#a855f7' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Settings size={16} /> {isLinux ? 'System Services' : 'Windows Services'}
            </button>
            <button 
              onClick={() => { setActiveTab('startup'); if(startupApps.length === 0) fetchStartupApps(); }}
              style={{ background: activeTab === 'startup' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'startup' ? '#eab308' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Activity size={16} /> {isLinux ? 'Startup Units' : 'Startup Apps'}
            </button>
          </div>
        </div>
        <button 
          onClick={() => { setActiveTab('events'); if(events.length === 0) fetchEvents(); }}
          style={{ 
            background: activeTab === 'events' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
            color: activeTab === 'events' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <AlertCircle size={18} color={activeTab === 'events' ? '#eab308' : 'currentColor'} /> {isLinux ? 'Journal' : 'Event Logs'}
        </button>
        <div className="nav-dropdown-container">
          <button 
            style={{ 
              background: (activeTab === 'network' || activeTab === 'firewall') ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
              color: (activeTab === 'network' || activeTab === 'firewall') ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
            }}
          >
            <ShieldCheck size={18} color={(activeTab === 'network' || activeTab === 'firewall') ? '#3b82f6' : 'currentColor'} /> Network Security ▼
          </button>
          <div className="nav-dropdown-menu">
            <button 
              onClick={() => { setActiveTab('network'); if(connections.length === 0) fetchConnections(); }}
              style={{ background: activeTab === 'network' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'network' ? '#3b82f6' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Network size={16} /> Active Connections
            </button>
            <button 
              onClick={() => { setActiveTab('firewall'); if(firewallRules.length === 0) fetchFirewallRules(); }}
              style={{ background: activeTab === 'firewall' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'firewall' ? '#ef4444' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <ShieldAlert size={16} /> {isLinux ? 'UFW Rules' : 'Firewall Rules'}
            </button>
          </div>
        </div>
        {!isLinux && (
          <button
            onClick={() => setActiveTab('registry')}
            style={{
              background: activeTab === 'registry' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer',
              color: activeTab === 'registry' ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
            }}
          >
            <Database size={18} color={activeTab === 'registry' ? '#f97316' : 'currentColor'} /> Registry
          </button>
        )}
        <div className="nav-dropdown-container">
          <button 
            style={{ 
              background: (activeTab === 'software' || activeTab === 'updates') ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
              color: (activeTab === 'software' || activeTab === 'updates') ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
            }}
          >
            <Package size={18} color={(activeTab === 'software' || activeTab === 'updates') ? '#ec4899' : 'currentColor'} /> Apps & Patches ▼
          </button>
          <div className="nav-dropdown-menu">
            <button 
              onClick={() => { setActiveTab('software'); if(softwareList.length === 0) fetchSoftware(); }}
              style={{ background: activeTab === 'software' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'software' ? '#ec4899' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Package size={16} /> Installed Software
            </button>
            <button 
              onClick={() => { setActiveTab('updates'); if(updates.length === 0) fetchUpdates(); }}
              style={{ background: activeTab === 'updates' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', color: activeTab === 'updates' ? '#14b8a6' : 'var(--text-secondary)', borderRadius: '4px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, transition: 'all 0.2s' }}
            >
              <Download size={16} /> {isLinux ? 'Package Updates' : 'Windows Updates'}
            </button>
          </div>
        </div>
        <button
          onClick={() => { setActiveTab('scheduled-tasks'); if(scheduledTasks.length === 0) { fetchScheduledTasks(); fetchRecentDeviceRuns(); } }}
          style={{
            background: activeTab === 'scheduled-tasks' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer',
            color: activeTab === 'scheduled-tasks' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <Clock size={18} color={activeTab === 'scheduled-tasks' ? '#8b5cf6' : 'currentColor'} /> Scheduled Tasks
        </button>
        <div style={{ width: '1px', background: 'var(--border)', margin: '0 0.5rem', alignSelf: 'stretch' }} />
        <button
          onClick={() => setActiveTab('terminal')}
          style={{ 
            background: activeTab === 'terminal' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
            color: activeTab === 'terminal' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <TerminalIcon size={18} color={activeTab === 'terminal' ? '#a8a29e' : 'currentColor'} /> Terminal
        </button>
        <button 
          onClick={() => { setActiveTab('files'); if(dirList.length === 0) fetchDirList('C:\\'); }}
          style={{ 
            background: activeTab === 'files' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
            color: activeTab === 'files' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <FolderGit2 size={18} color={activeTab === 'files' ? '#fbbf24' : 'currentColor'} /> Files
        </button>
        <button 
          onClick={() => setActiveTab('screenshot')}
          style={{ 
            background: activeTab === 'screenshot' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer', 
            color: activeTab === 'screenshot' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <Camera size={18} color={activeTab === 'screenshot' ? '#cbd5e1' : 'currentColor'} /> Screenshot
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          style={{
            background: activeTab === 'settings' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', padding: '0.75rem 1.25rem', cursor: 'pointer',
            color: activeTab === 'settings' ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, transition: 'all 0.2s'
          }}
        >
          <Settings size={18} color={activeTab === 'settings' ? '#94a3b8' : 'currentColor'} /> Settings
        </button>
      </div>

      {activeTab === 'dashboard' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(350px, 1fr) 2fr', gap: '2rem', marginBottom: '2rem' }}>
        {/* Hardware Specs Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          {hw && (
            <div className="glass-panel" style={{ padding: '1.5rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
                <Cpu size={20} color="var(--accent-primary)" /> System
              </h3>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p><strong>CPU:</strong> {hw.cpu}</p>
                <p><strong>Memory:</strong> {latestStat ? formatBytes(latestStat.ramTotal) : 'Unknown'} Total</p>
                <p><strong>Motherboard:</strong> {hw.motherboard}</p>
                <p><strong>System Model:</strong> {hw.model}</p>
              </div>

              {hw.ram && hw.ram.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Memory Slots</h4>
                  {hw.ram.map((r: any, i: number) => (
                    <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.5rem', borderLeft: '3px solid var(--success)' }}>
                      <p><strong>{r.bank}</strong>: {formatBytes(r.size)} {r.type} {r.clockSpeed ? `@ ${r.clockSpeed}MHz` : ''}</p>
                      <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '2px' }}>{r.manufacturer} | {r.partNum}</p>
                    </div>
                  ))}
                </div>
              )}

              {hw.gpus && hw.gpus.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>GPU(s)</h4>
                  {hw.gpus.map((g: any, i: number) => (
                    <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.5rem' }}>
                      <p><strong>{g.model}</strong> ({g.vendor})</p>
                      <p style={{ marginTop: '2px' }}>VRAM: {g.vram} MB | Driver: {g.driverVersion}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {latestStat?.battery && JSON.parse(latestStat.battery).hasBattery && (
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                  {(() => {
                    const bat = JSON.parse(latestStat.battery);
                    const degradation = (bat.maxCapacity && bat.designedCapacity) ? Math.round((bat.maxCapacity / bat.designedCapacity) * 100) : 100;
                    return (
                      <>
                        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                          <Battery size={16} color="var(--success)" /> Battery Health
                        </h4>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                           <p><strong>Charge:</strong> {bat.percent}% {bat.isCharging ? '(Charging)' : '(On Battery)'}</p>
                           {bat.cycleCount > 0 && <p><strong>Cycle Count:</strong> {bat.cycleCount}</p>}
                           <p>
                             <strong>Health Capacity:</strong> <span style={{ color: degradation < 80 ? 'var(--danger)' : 'var(--text-secondary)' }}>{degradation}%</span>
                           </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
              <HardDrive size={20} color="var(--warning)" /> Storage Disks
            </h3>
            {/* HW Physical Disks & Live Usage */}
            {(() => {
              const mappedMounts = new Set();
              
              const hardwareDiskElements = (hw?.disks || []).map((d: any, i: number) => {
                 const lds = d.mounts ? liveDisks.filter((l: any) => d.mounts.includes(l.mount)) : [];
                 lds.forEach((l: any) => mappedMounts.add(l.mount));
                 
                 return (
                   <div key={`d-${i}`} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                     <p style={{ color: 'var(--text-primary)' }}>
                       <strong>{d.name}</strong> ({d.type}) {d.mounts ? <span style={{ color: 'var(--success)', marginLeft: '4px' }}>[{d.mounts}]</span> : ''}
                       {d.smartStatus && d.smartStatus.toLowerCase() !== 'ok' && d.smartStatus.toLowerCase() !== 'unknown' && (
                         <span style={{ marginLeft: '8px', color: 'var(--danger)', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                           <AlertTriangle size={14} /> S.M.A.R.T: {d.smartStatus}
                         </span>
                       )}
                       {d.smartStatus && d.smartStatus.toLowerCase() === 'ok' && (
                         <span style={{ marginLeft: '8px', color: 'var(--success)', fontSize: '0.75rem' }}>
                           S.M.A.R.T: OK
                         </span>
                       )}
                     </p>
                     <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '2px', marginBottom: '8px' }}>
                       Physical Size: {formatBytes(d.size)} | Vendor: {d.vendor}
                     </p>
                     
                     {lds.map((ld: any, ldIdx: number) => {
                       const pct = Math.round((ld.used / ld.size) * 100) || 0;
                       return (
                         <div key={ldIdx} style={{ background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '6px', marginBottom: '0.5rem' }}>
                           <p style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                             <span><strong>{ld.mount}</strong> {formatBytes(ld.used)} used / {formatBytes(ld.size)}</span>
                             <span>{pct}%</span>
                           </p>
                           <div style={{ background: 'rgba(0,0,0,0.2)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                             <div style={{ width: `${pct}%`, background: pct > 90 ? 'var(--danger)' : 'var(--warning)', height: '100%', transition: 'width 0.5s ease' }} />
                           </div>
                         </div>
                       );
                     })}
                   </div>
                 );
              });

              const orphanElements = liveDisks.filter((l: any) => !mappedMounts.has(l.mount)).map((ld: any, i: number) => {
                 const pct = Math.round((ld.used / ld.size) * 100) || 0;
                 return (
                   <div key={`orphan-${i}`} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                     <p style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <strong>Dynamic Volume</strong> <span style={{ color: 'var(--success)' }}>[{ld.mount}]</span>
                     </p>
                     <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '2px', marginBottom: '8px' }}>
                       Live hot-plugged volume (Restart agent for physical details)
                     </p>
                     <div style={{ background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '6px' }}>
                       <p style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                         <span><strong>{ld.mount}</strong> {formatBytes(ld.used)} used / {formatBytes(ld.size)}</span>
                         <span>{pct}%</span>
                       </p>
                       <div style={{ background: 'rgba(0,0,0,0.2)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                         <div style={{ width: `${pct}%`, background: pct > 90 ? 'var(--danger)' : 'var(--warning)', height: '100%', transition: 'width 0.5s ease' }} />
                       </div>
                     </div>
                   </div>
                 );
              });

              return (
                <>
                  {hardwareDiskElements}
                  {orphanElements.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                       <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>Hot-Plugged Volumes</h4>
                       {orphanElements}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
              <Network size={20} color="#0ea5e9" /> Network Interfaces
            </h3>
            {hw?.network?.interfaces ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {hw.network.interfaces.map((iface: any, i: number) => (
                  <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.75rem', borderRadius: '6px' }}>
                    <p style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>
                      <strong>{iface.name}</strong> <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>({iface.type})</span>
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.75rem', opacity: 0.9 }}>
                      <p>IP Address: {iface.ip4} {iface.subnet ? `/${iface.subnet}` : ''}</p>
                      <p>MAC: {iface.mac}</p>
                      {iface.speed > 0 && <p>Link Speed: {iface.speed} Mbps</p>}
                    </div>
                  </div>
                ))}
                {hw.network.gateway && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <strong>Default Gateway:</strong> {hw.network.gateway}
                  </div>
                )}
                {hw.network.dns && hw.network.dns.length > 0 && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <strong>DNS Servers:</strong> {hw.network.dns.join(', ')}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>No network interface data available.</p>
            )}
            
            {hw?.network?.wifi && hw.network.wifi.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>WiFi Connections</h4>
                {hw.network.wifi.map((w: any, i: number) => (
                  <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.5rem' }}>
                    <p><strong>{w.ssid}</strong></p>
                    <p style={{ marginTop: '2px' }}>Channel: {w.channel} | Signal: {w.signalLevel} dBm</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hw.security && (
            <div className="glass-panel" style={{ padding: '1.5rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
                {hw.security.Defender?.RealTimeProtectionEnabled ? <ShieldCheck size={20} color="var(--success)" /> : <ShieldAlert size={20} color="var(--danger)" />} Security Posture
              </h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Antivirus & Defender</h4>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.5rem', borderRadius: '4px' }}>
                  {hw.security.AVProducts && (
                    <p><strong>3rd Party AV:</strong> {Array.isArray(hw.security.AVProducts) ? hw.security.AVProducts.map((a:any) => a.displayName).join(', ') : hw.security.AVProducts.displayName}</p>
                  )}
                  {hw.security.Defender && (
                    <>
                      <p style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <strong>Real-Time Protection:</strong> 
                        {hw.security.Defender.RealTimeProtectionEnabled ? <span style={{ color: 'var(--success)' }}>Active</span> : <span style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '4px' }}>Disabled <AlertTriangle size={14}/></span>}
                      </p>
                      <p style={{ marginTop: '4px' }}><strong>Signatures:</strong> {(() => {
                        const dStr = hw.security.Defender.AntivirusSignatureLastUpdated;
                        if (!dStr) return 'Unknown';
                        if (typeof dStr === 'string' && dStr.includes('/Date(')) {
                           const ms = parseInt(dStr.replace(/[^0-9]/g, ''), 10);
                           return new Date(ms).toLocaleDateString();
                        }
                        const d = new Date(dStr);
                        return d.toString() === 'Invalid Date' ? String(dStr) : d.toLocaleDateString();
                      })()}</p>
                      <p style={{ marginTop: '4px' }}><strong>AM Service:</strong> {hw.security.Defender.AMServiceEnabled ? 'Running' : 'Stopped'}</p>
                    </>
                  )}
                  {!hw.security.AVProducts && !hw.security.Defender && <p>No Antivirus telemetry found.</p>}
                </div>
              </div>

              {hw.security.Firewall && (
                <div>
                  <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Windows Firewall</h4>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '0.75rem', borderRadius: '4px', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {(Array.isArray(hw.security.Firewall) ? hw.security.Firewall : [hw.security.Firewall]).map((fw: any, idx: number) => {
                      const fwEnabled = fw.Enabled === true || fw.Enabled === 1 || String(fw.Enabled) === '1' || fw.Enabled === 'True';
                      return (
                        <div key={idx} style={{ flex: '1 1 auto', borderLeft: '2px solid var(--border)', paddingLeft: '8px' }}>
                          <p><strong>{fw.Name}</strong></p>
                          <p style={{ color: fwEnabled ? 'var(--success)' : 'var(--danger)', fontWeight: fwEnabled ? 'normal' : 'bold' }}>
                            {fwEnabled ? 'Active' : 'Disabled'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Live Graphs & Terminal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          <div className="glass-panel" style={{ padding: '1.5rem', flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, margin: 0 }}>
                <Activity size={20} color="var(--accent-primary)" /> Live Telemetry
              </h3>
              {displayStat && (
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '1rem', fontWeight: 600, alignItems: 'center' }}>
                   <span style={{ color: 'var(--accent-primary)' }}>CPU: {Math.round(displayStat.cpuUsage)}%</span>
                   <span style={{ color: 'var(--success)' }}>RAM: {Math.round((displayStat.ramUsed / displayStat.ramTotal) * 100)}%</span>
                   {(displayStat.cpuTemp ?? 0) > 0 && (
                     <span style={{ color: (displayStat.cpuTemp ?? 0) > 85 ? 'var(--danger)' : 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                       <Thermometer size={16} /> {Math.round(displayStat.cpuTemp!)}°C
                     </span>
                   )}
                </div>
              )}
            </div>

            <div style={{ height: '280px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" stroke="var(--text-secondary)" tick={{fontSize: 12}} minTickGap={50} />
                  <YAxis yAxisId="left" stroke="var(--text-secondary)" tick={{fontSize: 12}} domain={[0, 100]} />
                  <Tooltip isAnimationActive={false} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: 'none', borderRadius: '8px', color: 'white' }} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line yAxisId="left" type="monotone" dataKey="cpu" name="CPU Usage %" stroke="var(--accent-primary)" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="left" type="monotone" dataKey="ram" name="RAM Usage %" stroke="var(--success)" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="left" type="monotone" dataKey="temp" name="CPU Temp °C" stroke="#f59e0b" strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-panel" style={{ padding: '1.5rem', flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, margin: 0 }}>
                <Activity size={20} color="#0ea5e9" /> Live Network Bandwidth
              </h3>
              {displayStat && (
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '1rem', fontWeight: 600, alignItems: 'center' }}>
                   <span style={{ color: '#f43f5e' }}>Up: {displayStat.networkUp ? parseFloat(((displayStat.networkUp * 8) / 1000000).toFixed(2)) : 0} Mbps</span>
                   <span style={{ color: '#0ea5e9' }}>Down: {displayStat.networkDown ? parseFloat(((displayStat.networkDown * 8) / 1000000).toFixed(2)) : 0} Mbps</span>
                </div>
              )}
            </div>
            <div style={{ height: '220px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" stroke="var(--text-secondary)" tick={{fontSize: 12}} minTickGap={50} />
                  <YAxis stroke="var(--text-secondary)" tick={{fontSize: 12}} />
                  <Tooltip isAnimationActive={false} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: 'none', borderRadius: '8px', color: 'white' }} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line type="monotone" dataKey="upMbps" name="Upload (Mbps)" stroke="#f43f5e" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="downMbps" name="Download (Mbps)" stroke="#0ea5e9" strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-panel" style={{ padding: '1.5rem', flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, margin: 0 }}>
                <HardDrive size={20} color="#a855f7" /> Live Disk I/O
              </h3>
              {displayStat && (
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '1rem', fontWeight: 600, alignItems: 'center' }}>
                   <span style={{ color: '#a855f7' }}>Read: {displayStat.diskIoRead ? parseFloat((displayStat.diskIoRead / 1048576).toFixed(2)) : 0} MB/s</span>
                   <span style={{ color: '#eab308' }}>Write: {displayStat.diskIoWrite ? parseFloat((displayStat.diskIoWrite / 1048576).toFixed(2)) : 0} MB/s</span>
                </div>
              )}
            </div>
            <div style={{ height: '220px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" stroke="var(--text-secondary)" tick={{fontSize: 12}} minTickGap={50} />
                  <YAxis stroke="var(--text-secondary)" tick={{fontSize: 12}} />
                  <Tooltip isAnimationActive={false} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: 'none', borderRadius: '8px', color: 'white' }} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line type="monotone" dataKey="readMBps" name="Read (MB/s)" stroke="#a855f7" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="writeMBps" name="Write (MB/s)" stroke="#eab308" strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-panel" style={{ padding: '1.5rem', flex: 1 }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, margin: '0 0 1rem 0' }}>
              <HardDrive size={20} color="var(--warning)" /> 7-Day Storage History (Downsampled)
            </h3>
            <div style={{ height: '220px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={diskChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" stroke="var(--text-secondary)" tick={{fontSize: 12}} minTickGap={60} />
                  <YAxis stroke="var(--text-secondary)" tick={{fontSize: 12}} domain={[0, 100]} />
                  <Tooltip isAnimationActive={false} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: 'none', borderRadius: '8px', color: 'white' }} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  {liveDisks.length > 0 ? liveDisks.map((d: any, idx: number) => (
                    <Line key={d.fs} type="monotone" dataKey={d.fs} name={`${d.mount} Usage %`} stroke={['var(--warning)', 'var(--accent-primary)', 'var(--success)', '#8b5cf6'][idx % 4]} strokeWidth={2} dot={false} isAnimationActive={false} />
                  )) : (
                    <Line type="monotone" dataKey="Overall" name="Overall Disk Usage %" stroke="var(--warning)" strokeWidth={2} dot={false} isAnimationActive={false} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      )}

      {['processes', 'services', 'startup', 'events', 'network', 'firewall', 'registry', 'software', 'updates'].includes(activeTab) && (
        <div className="glass-panel" style={{ padding: '1.5rem', minHeight: '600px' }}>
            {activeTab === 'firewall' ? (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                   <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                     <input type="text" placeholder="Filter Firewall Rules..." value={firewallFilter} onChange={e => setFirewallFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-primary)', width: '300px' }} />
                     {firewallLoading && <RefreshCw size={16} className="spin" color="var(--accent-primary)" />}
                   </div>
                   <button onClick={fetchFirewallRules} className="btn" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                     <RefreshCw size={14} /> Refresh
                   </button>
                </div>
                {firewallError && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{firewallError}</p>}
                
                <div style={{ overflowX: 'auto', maxHeight: '550px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 10 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestFirewallSort('DisplayName')}>Rule Name {firewallSortConfig?.key === 'DisplayName' ? (firewallSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestFirewallSort('Direction')}>Direction {firewallSortConfig?.key === 'Direction' ? (firewallSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestFirewallSort('Action')}>Action {firewallSortConfig?.key === 'Action' ? (firewallSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestFirewallSort('Profile')}>Profile {firewallSortConfig?.key === 'Profile' ? (firewallSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem' }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sliceRows(sortedAndFilteredFirewallRules, 'firewall').map((r: any, idx: number) => {
                         const isAllow = (r.Action === 2 || r.Action === 4 || String(r.Action).toLowerCase() === 'allow');
                         const profileName = r.Profile === 1 ? 'Domain' : r.Profile === 2 ? 'Private' : r.Profile === 4 ? 'Public' : r.Profile === 2147483647 ? 'Any' : r.Profile;
                         return (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s', opacity: r.Enabled === 1 || r.Enabled === 2 || String(r.Enabled).toLowerCase() === 'true' ? 1 : 0.5 }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>{r.DisplayName || '-'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>{r.Direction === 1 || String(r.Direction).toLowerCase() === 'inbound' ? 'Inbound' : 'Outbound'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: isAllow ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>{isAllow ? 'Allow' : 'Block'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>{profileName}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.Description}>{r.Description || '-'}</td>
                        </tr>
                      )})}
                      {sortedAndFilteredFirewallRules.length > TABLE_ROW_LIMIT && (
                        <tr>
                          <td colSpan={5} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <button onClick={() => toggleTableExpand('firewall')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent-primary)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              {expandedTables.has('firewall') ? `Show first ${TABLE_ROW_LIMIT}` : `Show all ${sortedAndFilteredFirewallRules.length} rows`}
                            </button>
                          </td>
                        </tr>
                      )}
                      {sortedAndFilteredFirewallRules.length === 0 && !firewallLoading && (
                        <tr>
                          <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No firewall rules found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'startup' ? (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                   <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                     <input type="text" placeholder="Filter Autostart Entries..." value={startupFilter} onChange={e => setStartupFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-primary)', width: '300px' }} />
                     {startupLoading && <RefreshCw size={16} className="spin" color="var(--accent-primary)" />}
                   </div>
                   <button onClick={fetchStartupApps} className="btn" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                     <RefreshCw size={14} /> Refresh
                   </button>
                </div>
                {startupError && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{startupError}</p>}
                
                <div style={{ overflowX: 'auto', maxHeight: '550px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 10 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestStartupSort('Name')}>Name {startupSortConfig?.key === 'Name' ? (startupSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestStartupSort('Command')}>Command {startupSortConfig?.key === 'Command' ? (startupSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestStartupSort('Location')}>Location {startupSortConfig?.key === 'Location' ? (startupSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestStartupSort('User')}>Context {startupSortConfig?.key === 'User' ? (startupSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sliceRows(sortedAndFilteredStartupApps, 'startup').map((a: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.Name}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.Command}>{a.Command || '-'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.Location}>{a.Location || '-'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>{a.User || 'System'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                            <button onClick={() => deleteStartupApp(a.Name, a.Location)} style={{ background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                               Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {sortedAndFilteredStartupApps.length === 0 && !startupLoading && (
                        <tr>
                          <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No startup applications found matching that filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'processes' ? (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                   <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                     <input type="text" placeholder="Filter by Name, PID, or Command..." value={processFilter} onChange={e => setProcessFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: 'var(--text-primary)', width: '300px' }} />
                     {processesLoading && <RefreshCw size={16} className="spin" color="var(--accent-primary)" />}
                   </div>
                   <button onClick={fetchProcesses} className="btn" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                     <RefreshCw size={14} /> Refresh
                   </button>
                </div>
                {processesError && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{processesError}</p>}
                
                <div style={{ overflowX: 'auto', maxHeight: '550px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 10 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestProcessSort('pid')}>PID {processSortConfig?.key === 'pid' ? (processSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestProcessSort('name')}>Name {processSortConfig?.key === 'name' ? (processSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestProcessSort('cpu')}>CPU % {processSortConfig?.key === 'cpu' ? (processSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem', cursor: 'pointer' }} onClick={() => requestProcessSort('mem')}>Memory {processSortConfig?.key === 'mem' ? (processSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.75rem' }}>Command Line</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sliceRows(sortedAndFilteredProcesses, 'processes').map((p: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>{p.pid}</td>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{p.name}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: p.cpu > 50 ? 'var(--danger)' : p.cpu > 20 ? 'var(--warning)' : 'var(--text-primary)' }}>{p.cpu ? p.cpu.toFixed(1) : 0}%</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: p.mem > 50 ? 'var(--danger)' : p.mem > 20 ? 'var(--warning)' : 'var(--text-primary)' }}>{p.mem ? p.mem.toFixed(1) : 0}%</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.command}>{p.command || '-'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                            <button onClick={() => executeKill(p.pid, p.name)} disabled={killLoading === p.pid} style={{ background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                               {killLoading === p.pid ? 'Killing...' : 'Kill Process'}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {sortedAndFilteredProcesses.length > TABLE_ROW_LIMIT && (
                        <tr>
                          <td colSpan={6} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <button onClick={() => toggleTableExpand('processes')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent-primary)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              {expandedTables.has('processes') ? `Show first ${TABLE_ROW_LIMIT}` : `Show all ${sortedAndFilteredProcesses.length} rows`}
                            </button>
                          </td>
                        </tr>
                      )}
                      {sortedAndFilteredProcesses.length === 0 && !processesLoading && (
                        <tr>
                          <td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No processes found matching that filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'services' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                  <input 
                    type="text" 
                    placeholder="Search services..." 
                    value={serviceFilter}
                    onChange={e => setServiceFilter(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                  />
                  <button className="btn btn-secondary" onClick={fetchServices} disabled={servicesLoading}>
                    <RefreshCw size={14} className={servicesLoading ? 'spin' : ''} /> Refresh
                  </button>
                </div>
                
                {serviceError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{serviceError}</p>}
                
                <div style={{ maxHeight: '550px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestSort('displayName')}>
                          Display Name {sortConfig?.key === 'displayName' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestSort('name')}>
                          Name {sortConfig?.key === 'name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestSort('running')}>
                          Status {sortConfig?.key === 'running' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestSort('startmode')}>
                          Start Mode {sortConfig?.key === 'startmode' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sliceRows(filteredServices, 'services').map((s, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)' }}>{s.displayName || s.name}</td>
                          <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{s.name}</td>
                          <td style={{ padding: '0.5rem', color: s.running ? 'var(--success)' : 'var(--text-secondary)' }}>{s.running ? 'Running' : 'Stopped'}</td>
                          <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{s.startmode || 'Unknown'}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            {s.running ? (
                              <>
                                <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={() => handleServiceAction(s.name, 'stop')} disabled={serviceActionLoading === s.name}>
                                  <Square size={12} /> Stop
                                </button>
                                <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }} onClick={() => handleServiceAction(s.name, 'restart')} disabled={serviceActionLoading === s.name}>
                                  <RefreshCw size={12} /> Restart
                                </button>
                              </>
                            ) : (
                              <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)', borderColor: 'rgba(34, 197, 94, 0.3)' }} onClick={() => handleServiceAction(s.name, 'start')} disabled={serviceActionLoading === s.name}>
                                <Play size={12} /> Start
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredServices.length > TABLE_ROW_LIMIT && (
                        <tr>
                          <td colSpan={5} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <button onClick={() => toggleTableExpand('services')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent-primary)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              {expandedTables.has('services') ? `Show first ${TABLE_ROW_LIMIT}` : `Show all ${filteredServices.length} rows`}
                            </button>
                          </td>
                        </tr>
                      )}
                      {servicesLoading && services.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem', color: '#a855f7' }} />
                            <p>Querying System Services...</p>
                          </td>
                        </tr>
                      )}
                      {services.length === 0 && !servicesLoading && (
                        <tr>
                          <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No services found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'events' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Showing last 50 Critical/Error system events.</p>
                  <button className="btn btn-secondary" onClick={fetchEvents} disabled={eventsLoading}>
                    <RefreshCw size={14} className={eventsLoading ? 'spin' : ''} /> Refresh
                  </button>
                </div>
                
                {eventsError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{eventsError}</p>}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                  {eventsLoading && events.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem', color: 'var(--danger)' }} />
                      <p>Querying Eventvwr natively...</p>
                    </div>
                  ) : events.length === 0 ? (
                    <div style={{ border: '1px solid var(--border)', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: 'var(--success)', background: 'rgba(34, 197, 94, 0.05)' }}>
                      No recent Critical or Error events found!
                    </div>
                  ) : events.map((ev, i) => {
                     let dStr = ev.TimeCreated;
                     let formattedDate = 'Unknown Time';
                     if (dStr) {
                       if (typeof dStr === 'string' && dStr.includes('/Date(')) {
                         const ms = parseInt(dStr.replace(/[^0-9]/g, ''), 10);
                         formattedDate = new Date(ms).toLocaleString();
                       } else {
                         const d = new Date(dStr);
                         formattedDate = d.toString() === 'Invalid Date' ? String(dStr) : d.toLocaleString();
                       }
                     }
                     return (
                       <div key={i} style={{ borderLeft: `3px solid ${ev.LevelDisplayName === 'Critical' ? 'var(--danger)' : '#f59e0b'}`, background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '0 6px 6px 0' }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                           <strong style={{ color: ev.LevelDisplayName === 'Critical' ? 'var(--danger)' : '#f59e0b' }}>{ev.LevelDisplayName} - {ev.ProviderName} (EventID: {ev.Id})</strong>
                           <span style={{ color: 'var(--text-secondary)' }}>{formattedDate}</span>
                         </div>
                         <p style={{ fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', margin: 0, overflowWrap: 'break-word', wordWrap: 'break-word' }}>{ev.Message}</p>
                       </div>
                     );
                  })}
                </div>
              </div>
            ) : activeTab === 'network' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Mapping all active outbound TCP Sockets via <code>Get-NetTCPConnection</code></p>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input 
                      type="text" 
                      placeholder="Filter explicit ip, process, 'trusted', or 'local'..." 
                      value={connectionFilter}
                      onChange={e => setConnectionFilter(e.target.value)}
                      style={{ padding: '0.4rem 0.8rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: 'white', fontSize: '0.875rem', minWidth: '260px' }}
                    />
                    <button className="btn btn-secondary" onClick={fetchConnections} disabled={connectionsLoading}>
                      <RefreshCw size={14} className={connectionsLoading ? 'spin' : ''} /> Refresh
                    </button>
                  </div>
                </div>
                
                {connectionsError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{connectionsError}</p>}
                
                <div style={{ maxHeight: '500px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestConnectionSort('ProcessName')}>Process (PID) {connectionSortConfig?.key === 'ProcessName' ? (connectionSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.5rem' }}>Local Address</th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestConnectionSort('RemoteAddress')}>Remote Target {connectionSortConfig?.key === 'RemoteAddress' ? (connectionSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestConnectionSort('trusted_local')}>Trust {connectionSortConfig?.key === 'trusted_local' ? (connectionSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.5rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => requestConnectionSort('State')}>State {connectionSortConfig?.key === 'State' ? (connectionSortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sliceRows(sortedAndFilteredConnections, 'connections').map((c, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)' }} title={c.CommandLine ? `Command Line Launch Payload:\n----------------------------\n${c.CommandLine}` : 'No explicit launch arguments detected.'}>
                             <span style={{ color: '#06b6d4' }}>{c.ProcessName || 'Unknown'}</span> <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>({c.PID})</span>
                             {c.Services && <div style={{ fontSize: '0.75rem', color: '#a855f7', marginTop: '2px' }}>{c.Services}</div>}
                          </td>
                          <td style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{c.LocalAddress}:{c.LocalPort}</td>
                          <td style={{ padding: '0.5rem', wordBreak: 'break-all' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {c.Geo && c.Geo.country_code && (
                                  <img 
                                    src={`https://flagcdn.com/w20/${c.Geo.country_code.toLowerCase()}.png`} 
                                    alt={c.Geo.country_code} 
                                    title={c.Geo.country} 
                                    style={{ width: '20px', borderRadius: '2px', objectFit: 'contain' }} 
                                  />
                                )}
                                <span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{c.RemoteAddress}:{c.RemotePort}</span>
                              </div>
                              {c.Hostnames && c.Hostnames.length > 0 && (
                                <span style={{ fontSize: '0.7rem', color: '#8b5cf6', fontFamily: 'monospace' }}>{c.Hostnames[0]}</span>
                              )}
                              {c.Geo && c.Geo.organization_name && (!c.Hostnames || c.Hostnames.length === 0) && (
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{c.Geo.organization_name}</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            {isTrustedOrg(c.Geo?.organization_name, c.Hostnames) ? (
                              <span title="Trusted Infrastructure" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--success)' }}><ShieldCheck size={14} /> Trusted</span>
                            ) : isLocalIp(c.RemoteAddress) ? (
                              <span title="Local Network Infrastructure" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--success)' }}><Home size={14} /> Local</span>
                            ) : (
                              <span title="Untrusted Public IP" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--warning)' }}><AlertTriangle size={14} /> Public</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem', color: (c.State === 'Established' || c.State === 5) ? 'var(--success)' : 'var(--text-secondary)' }}>{getTcpState(c.State)}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button
                               className="btn btn-secondary"
                               style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                               onClick={() => executeKill(c.PID, c.ProcessName || 'Unknown Process')}
                               disabled={killLoading === c.PID || c.PID === 0 || c.ProcessName === 'System' || c.ProcessName === 'Idle'}
                            >
                              <ShieldOff size={12} style={{ marginRight: '4px' }} /> Kill Process
                            </button>
                            <button
                               className="btn btn-secondary"
                               style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)', borderColor: 'rgba(245, 158, 11, 0.3)' }}
                               onClick={() => blockIp(c.RemoteAddress)}
                               disabled={blockIpLoading === c.RemoteAddress}
                            >
                              <Ban size={12} style={{ marginRight: '4px' }} /> {blockIpLoading === c.RemoteAddress ? 'Blocking...' : 'Block IP'}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {sortedAndFilteredConnections.length > TABLE_ROW_LIMIT && (
                        <tr>
                          <td colSpan={6} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <button onClick={() => toggleTableExpand('connections')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent-primary)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                              {expandedTables.has('connections') ? `Show first ${TABLE_ROW_LIMIT}` : `Show all ${sortedAndFilteredConnections.length} rows`}
                            </button>
                          </td>
                        </tr>
                      )}
                      {connectionsLoading && connections.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem', color: '#06b6d4' }} />
                            <p>Mapping all active outbound TCP Sockets via Get-NetTCPConnection...</p>
                          </td>
                        </tr>
                      )}
                      {connections.length === 0 && !connectionsLoading && (
                        <tr>
                          <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No active outbound TCP connections mapped.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'registry' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button className="btn btn-secondary" onClick={() => {
                     const parts = registryPath.split('\\');
                     if (parts.length > 1) {
                         parts.pop();
                         fetchRegistry(parts.join('\\'));
                     } else {
                         fetchRegistry('');
                     }
                  }} disabled={!registryPath || registryLoading}>
                    <ArrowLeft size={16} /> Up One Level
                  </button>
                  <input 
                    type="text" 
                    value={registryPath} 
                    onChange={e => setRegistryPath(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                    placeholder="e.g. HKLM:\Software"
                    onKeyDown={e => e.key === 'Enter' && fetchRegistry(registryPath)}
                  />
                  <button className="btn btn-secondary" onClick={() => fetchRegistry(registryPath)} disabled={registryLoading}>
                    <RefreshCw size={14} className={registryLoading ? 'spin' : ''} /> {registryLoading ? 'Querying...' : 'Refresh API'}
                  </button>
                </div>
                
                {registryError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{registryError}</p>}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 2fr', gap: '1rem', alignItems: 'start' }}>
                  <div style={{ height: '450px', display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left', display: 'block', height: '100%' }}>
                      <thead style={{ display: 'table', width: '100%', tableLayout: 'fixed', background: '#1e293b' }}>
                        <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                           <th style={{ padding: '0.5rem' }}>Registry Sub-Keys</th>
                        </tr>
                      </thead>
                      <tbody style={{ display: 'block', overflowY: 'auto', height: 'calc(100% - 33px)', width: '100%' }}>
                        {registryData.Keys.map((k, idx) => (
                           <tr key={idx} style={{ display: 'table', width: '100%', tableLayout: 'fixed', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }} onClick={() => fetchRegistry(registryPath ? `${registryPath}\\${k.Name || k.PSChildName}` : k.Name || k.PSChildName)}>
                             <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                               <FolderOpen size={14} color="var(--warning)"/> {k.Name || k.PSChildName}
                             </td>
                           </tr>
                        ))}
                        {registryData.Keys.length === 0 && !registryLoading && (
                           <tr style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}><td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No sub-keys</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ height: '450px', display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left', display: 'block', height: '100%' }}>
                      <thead style={{ display: 'table', width: '100%', tableLayout: 'fixed', background: '#1e293b' }}>
                        <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                           <th style={{ padding: '0.5rem', width: '30%' }}>Name</th>
                           <th style={{ padding: '0.5rem', width: '20%' }}>Type</th>
                           <th style={{ padding: '0.5rem', width: '50%' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody style={{ display: 'block', overflowY: 'auto', height: 'calc(100% - 33px)', width: '100%' }}>
                        {registryData.Values.map((v, idx) => (
                           <tr key={idx} style={{ display: 'table', width: '100%', tableLayout: 'fixed', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                             <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)', width: '30%', wordBreak: 'break-all' }}>{v.Name}</td>
                             <td style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.75rem', width: '20%' }}>{v.Type}</td>
                             <td style={{ padding: '0.5rem', color: '#3b82f6', wordBreak: 'break-all', width: '50%' }}>{String(v.Value)}</td>
                           </tr>
                        ))}
                        {registryData.Values.length === 0 && !registryLoading && (
                           <tr style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}><td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No properties</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : activeTab === 'updates' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Querying Native Microsoft.Update.Session COM Object</p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-secondary" onClick={fetchUpdates} disabled={updatesLoading || updateActionLoading}>
                      <RefreshCw size={14} className={updatesLoading ? 'spin' : ''} /> Check
                    </button>
                    {updates.length > 0 && (
                      <button className="btn btn-primary" onClick={installUpdates} disabled={updatesLoading || updateActionLoading} style={{ background: 'var(--success)', borderColor: 'var(--success)' }}>
                        <Download size={14} /> Install All Missing Updates {updateActionLoading && '(Installing...)'}
                      </button>
                    )}
                  </div>
                </div>
                
                {updatesError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{updatesError}</p>}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                  {updatesLoading && updates.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem', color: '#3b82f6' }} />
                      <p>Querying OS Patch Database in the background...<br/>
                      <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>(This process can take up to 2 minutes on slower hard disks)</span></p>
                    </div>
                  ) : updates.length === 0 ? (
                    <div style={{ border: '1px solid var(--border)', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: 'var(--success)', background: 'rgba(34, 197, 94, 0.05)' }}>
                      No missing KB patches found. Windows is fully up-to-date!
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                        <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                          <th style={{ padding: '0.5rem' }}>Update Title</th>
                          <th style={{ padding: '0.5rem' }}>KB Article</th>
                          <th style={{ padding: '0.5rem' }}>Mandatory</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updates.map((u, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)' }}>{u.Title}</td>
                            <td style={{ padding: '0.5rem', color: '#3b82f6' }}>{u.KB || 'Unknown'}</td>
                            <td style={{ padding: '0.5rem', color: u.IsMandatory ? 'var(--danger)' : 'var(--text-secondary)' }}>{u.IsMandatory ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ) : activeTab === 'software' ? (
              <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                  <input 
                    type="text" 
                    placeholder="Search installed software..." 
                    value={softwareFilter}
                    onChange={e => setSoftwareFilter(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                  />
                  <button className="btn btn-secondary" onClick={fetchSoftware} disabled={softwareLoading}>
                    <RefreshCw size={14} className={softwareLoading ? 'spin' : ''} /> Refresh
                  </button>
                </div>
                
                {softwareError && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{softwareError}</p>}
                
                <div style={{ maxHeight: '500px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.5rem' }}>Application Name</th>
                        <th style={{ padding: '0.5rem' }}>Publisher</th>
                        <th style={{ padding: '0.5rem' }}>Version</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = softwareList.filter(s => s.DisplayName && s.DisplayName.toLowerCase().includes(softwareFilter.toLowerCase()));
                        return (<>
                          {sliceRows(filtered, 'software').map((sw, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <td style={{ padding: '0.5rem', fontWeight: 500, color: 'var(--text-primary)' }}>{sw.DisplayName}</td>
                              <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{sw.Publisher || 'Unknown'}</td>
                              <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{sw.DisplayVersion || '-'}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                <button
                                   className="btn btn-secondary"
                                   style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                                   onClick={() => executeUninstall(sw.DisplayName, sw.UninstallString)}
                                   disabled={uninstallLoading === sw.DisplayName}
                                >
                                  <Square size={12} style={{ marginRight: '4px' }} /> Uninstall
                                </button>
                              </td>
                            </tr>
                          ))}
                          {filtered.length > TABLE_ROW_LIMIT && (
                            <tr>
                              <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'center' }}>
                                <button onClick={() => toggleTableExpand('software')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent-primary)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                  {expandedTables.has('software') ? `Show first ${TABLE_ROW_LIMIT}` : `Show all ${filtered.length} rows`}
                                </button>
                              </td>
                            </tr>
                          )}
                        </>);
                      })()}
                      {softwareLoading && softwareList.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem', color: '#8b5cf6' }} />
                            <p>Querying installed software registry arrays...</p>
                          </td>
                        </tr>
                      )}
                      {softwareList.length === 0 && !softwareLoading && (
                        <tr>
                          <td colSpan={4} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No software mapping found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
      )}

      {activeTab === 'terminal' && (
          <div className="glass-panel" style={{ padding: '1.5rem', minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, margin: 0 }}>
                <TerminalIcon size={20} color="var(--text-primary)" /> Interactive Terminal
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {termConnected && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} /> Connected
                  </span>
                )}
                {termConnected && (
                  <div style={{ position: 'relative' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => { if (showScriptPicker === 'terminal') { setShowScriptPicker(null); } else { fetchLibraryScripts(); setShowScriptPicker('terminal'); } }}>
                      <FileText size={14} /> Library
                    </button>
                    <ScriptPickerDropdown target="terminal" />
                  </div>
                )}
                {!termConnected ? (
                  <button
                    className="btn"
                    onClick={connectTerminal}
                    disabled={termConnecting || device?.status !== 'online'}
                  >
                    {termConnecting ? 'Connecting...' : 'Connect'}
                  </button>
                ) : (
                  <button className="btn btn-secondary" onClick={disconnectTerminal}>
                    Disconnect
                  </button>
                )}
              </div>
            </div>

            {termExited && !termConnected && (
              <p style={{ color: 'var(--warning)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                Session ended. Click Connect to start a new session.
              </p>
            )}

            <div
              ref={termContainerRef}
              style={{
                flex: 1,
                minHeight: '450px',
                background: '#0f172a',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                padding: '4px',
                overflow: 'hidden',
              }}
            />

            {!termConnected && !termConnecting && !termExited && (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                Click Connect to open a live PowerShell session on this device. The session runs as SYSTEM in Session 0.
              </p>
            )}
          </div>
      )}
          
      {activeTab === 'files' && (
          <div className="glass-panel" style={{ padding: '1.5rem', minHeight: '600px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
              <FolderGit2 size={20} color="var(--warning)" /> Remote File Explorer
            </h3>
            
            <form onSubmit={e => { e.preventDefault(); fetchDirList(currentPath); }} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input 
                type="text" 
                value={currentPath}
                onChange={e => setCurrentPath(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-primary">Go</button>
              
              <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={!currentPath || currentPath === ''}>
                <UploadCloud size={16} /> Upload Here
              </button>
              <input type="file" ref={fileInputRef} onChange={uploadFile} style={{ display: 'none' }} />
            </form>
            
            {fileStatus && <p style={{ color: 'var(--success)', fontSize: '0.875rem', marginBottom: '1rem' }}>{fileStatus}</p>}

            <div style={{ maxHeight: '400px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '0.5rem 1rem' }}>Name</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Size</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Modified</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPath !== 'C:\\' && currentPath !== '/' && currentPath !== '' && (
                    <tr 
                      onClick={() => {
                        const parts = currentPath.split(/[\\/]/).filter(Boolean);
                        parts.pop();
                        const parent = currentPath.includes('\\') ? parts.join('\\') + '\\' : '/' + parts.join('/');
                        fetchDirList(parent || (currentPath.includes('\\') ? 'C:\\' : '/'));
                      }}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td colSpan={3} style={{ padding: '0.5rem 1rem', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FolderOpen size={16} /> .. (Up a directory)
                      </td>
                    </tr>
                  )}
                  {dirList.map((f, i) => (
                    <tr 
                      key={i} 
                      onClick={() => {
                        if (f.isDir) {
                          const sep = currentPath.includes('\\') ? '\\' : '/';
                          fetchDirList(currentPath.endsWith(sep) ? currentPath + f.name : currentPath + sep + f.name);
                        } else {
                          const sep = currentPath.includes('\\') ? '\\' : '/';
                          const fullPath = currentPath.endsWith(sep) ? currentPath + f.name : currentPath + sep + f.name;
                          downloadFile(fullPath, f.name);
                        }
                      }}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '8px', color: f.isDir ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
                        {f.isDir ? <FolderOpen size={16} /> : <FileText size={16} />}
                        {f.name}
                      </td>
                      <td style={{ padding: '0.5rem 1rem', color: 'var(--text-secondary)' }}>{f.isDir ? '-' : formatBytes(f.size)}</td>
                      <td style={{ padding: '0.5rem 1rem', color: 'var(--text-secondary)' }}>{new Date(f.mtime).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
      )}
      
      {activeTab === 'screenshot' && (
      <div className="glass-panel" style={{ padding: '1.5rem', minHeight: '600px' }} ref={screenshotRef}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
          <Camera size={20} color="var(--accent-primary)" /> Remote Screenshot
        </h3>

        {isLinux ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Screenshots are not supported on Linux/Wayland.</p>
            <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>Use your desktop environment's screenshot tool instead.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <button className="btn btn-primary" onClick={requestScreenshot} disabled={screenshotStatus.includes('Capturing')} style={{ background: 'var(--accent-primary)', color: '#000' }}>
                <Camera size={16} /> Capture New Screenshot
              </button>

              {screenshotStatus && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '1rem' }}>
                  {screenshotStatus}
                </p>
              )}
            </div>

            {screenshotBase64s.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {screenshotBase64s.map((b64: string, idx: number) => (
                  <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: '#000' }}>
                    <p style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.05)', margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Display {idx + 1}</p>
                    <img src={`data:image/jpeg;base64,${b64}`} alt={`Remote Screen ${idx + 1}`} style={{ width: '100%', height: 'auto', display: 'block' }} />
                  </div>
                ))}
              </div>
            ) : screenshotBase64 ? (
              <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: '#000' }}>
                <img src={`data:image/jpeg;base64,${screenshotBase64}`} alt="Remote Screen" style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
            ) : null}
          </>
        )}
      </div>
      )}

      {activeTab === 'scheduled-tasks' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Header with New Task button */}
          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={20} color="#8b5cf6" /> Scheduled Tasks
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={() => { fetchScheduledTasks(); fetchRecentDeviceRuns(); }}>
                  <RefreshCw size={14} /> Refresh
                </button>
                <button className="btn" onClick={() => { setShowTaskForm(true); setEditingTask(null); setTaskForm({ name: '', command: '', cron: '0 3 * * 0', enabled: true }); setTaskFormError(''); }}>
                  + New Task
                </button>
              </div>
            </div>

            {scheduledTasksError && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{scheduledTasksError}</p>}

            {/* Task Form (create/edit) */}
            {showTaskForm && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid var(--border)' }}>
                <h4 style={{ margin: '0 0 1rem 0' }}>{editingTask ? 'Edit Task' : 'New Scheduled Task'}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Name</label>
                    <input type="text" value={taskForm.name} onChange={e => setTaskForm({ ...taskForm, name: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)' }}
                      placeholder="e.g., Weekly Disk Cleanup" />
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Command</label>
                      <div style={{ position: 'relative' }}>
                        <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                          onClick={() => { if (showScriptPicker === 'task') { setShowScriptPicker(null); } else { fetchLibraryScripts(); setShowScriptPicker('task'); } }}>
                          <FileText size={12} /> From Library
                        </button>
                        <ScriptPickerDropdown target="task" />
                      </div>
                    </div>
                    <textarea value={taskForm.command} onChange={e => setTaskForm({ ...taskForm, command: e.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)', fontFamily: 'monospace', resize: 'vertical' }}
                      placeholder="e.g., cleanmgr /sagerun:1" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Cron Expression
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input type="text" value={taskForm.cron} onChange={e => setTaskForm({ ...taskForm, cron: e.target.value })}
                        style={{ flex: 1, padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)', fontFamily: 'monospace' }}
                        placeholder="0 3 * * 0" />
                      <select onChange={e => { if (e.target.value) setTaskForm({ ...taskForm, cron: e.target.value }); }}
                        style={{ padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-secondary)' }}>
                        <option value="">Presets...</option>
                        <option value="0 * * * *">Hourly</option>
                        <option value="0 3 * * *">Daily (3am)</option>
                        <option value="0 3 * * 0">Weekly (Sun 3am)</option>
                        <option value="0 3 1 * *">Monthly (1st 3am)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" checked={taskForm.enabled} onChange={e => setTaskForm({ ...taskForm, enabled: e.target.checked })} id="task-enabled" />
                    <label htmlFor="task-enabled" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Enabled</label>
                  </div>
                  {taskFormError && <p style={{ color: 'var(--danger)', margin: 0, fontSize: '0.85rem' }}>{taskFormError}</p>}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button className="btn" onClick={saveTask} disabled={taskFormSaving}>
                      {taskFormSaving ? 'Saving...' : (editingTask ? 'Update Task' : 'Create Task')}
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setShowTaskForm(false); setEditingTask(null); setTaskFormError(''); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Task List Table */}
            {scheduledTasksLoading ? <p style={{ color: 'var(--text-secondary)' }}>Loading...</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Schedule</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Last Run</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Status</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Next Run</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Enabled</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledTasks.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No scheduled tasks yet. Click "New Task" to create one.</td></tr>
                  )}
                  {scheduledTasks.map((task: any) => {
                    const lastRun = task.runs?.[0];
                    const statusColor = lastRun?.status === 'success' ? 'var(--success)' : lastRun?.status === 'failed' ? 'var(--danger)' : lastRun?.status === 'missed' ? 'var(--warning)' : 'var(--text-secondary)';
                    return (
                      <tr key={task.id} style={{ borderBottom: '1px solid var(--glass-border)', cursor: 'pointer' }}
                        onClick={() => fetchTaskRuns(task.id)}>
                        <td style={{ padding: '0.75rem 0.5rem', fontWeight: 500 }}>{task.name}</td>
                        <td style={{ padding: '0.75rem 0.5rem', fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{task.cron}</td>
                        <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {lastRun ? new Date(lastRun.startedAt).toLocaleString() : '\u2014'}
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem' }}>
                          {lastRun ? (
                            <span style={{ background: statusColor, color: lastRun.status === 'missed' ? '#000' : '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                              {lastRun.status}
                            </span>
                          ) : <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{'\u2014'}</span>}
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {task.enabled ? new Date(task.nextRun).toLocaleString() : '\u2014'}
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem' }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => toggleTaskEnabled(task)}
                            style={{ background: task.enabled ? 'var(--success)' : 'var(--bg-elevated)', border: 'none', color: task.enabled ? '#fff' : 'var(--text-secondary)', padding: '2px 10px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                            {task.enabled ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              onClick={() => { setEditingTask(task); setTaskForm({ name: task.name, command: task.command, cron: task.cron, enabled: task.enabled }); setShowTaskForm(true); setTaskFormError(''); }}>
                              Edit
                            </button>
                            <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              onClick={() => runTaskNow(task.id)} disabled={runNowLoading === task.id}>
                              {runNowLoading === task.id ? '...' : 'Run Now'}
                            </button>
                            <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--danger)' }}
                              onClick={() => deleteTask(task.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Run History for selected task */}
          {selectedTaskId && (
            <div className="glass-panel" style={{ padding: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem 0' }}>
                Run History {'\u2014'} {scheduledTasks.find((t: any) => t.id === selectedTaskId)?.name || 'Task'}
              </h4>
              {taskRunsLoading ? <p style={{ color: 'var(--text-secondary)' }}>Loading...</p> : (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Timestamp</th>
                        <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Triggered By</th>
                        <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Status</th>
                        <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Duration</th>
                        <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Output</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskRuns.length === 0 && (
                        <tr><td colSpan={5} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No runs yet</td></tr>
                      )}
                      {taskRuns.map((run: any) => {
                        const statusColor = run.status === 'success' ? 'var(--success)' : run.status === 'failed' ? 'var(--danger)' : 'var(--warning)';
                        const duration = run.completedAt ? Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000) : null;
                        return (
                          <tr key={run.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                            <td style={{ padding: '0.5rem', fontSize: '0.85rem' }}>{new Date(run.startedAt).toLocaleString()}</td>
                            <td style={{ padding: '0.5rem' }}>
                              <span style={{ background: run.triggeredBy === 'manual' ? 'var(--accent-primary)' : 'var(--bg-elevated)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>
                                {run.triggeredBy}
                              </span>
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              <span style={{ background: statusColor, color: run.status === 'missed' ? '#000' : '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                                {run.status}
                              </span>
                            </td>
                            <td style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              {duration !== null ? `${duration}s` : '\u2014'}
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              {run.output ? (
                                <details>
                                  <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    {run.output.substring(0, 80)}{run.output.length > 80 ? '...' : ''}
                                  </summary>
                                  <pre style={{ background: 'var(--bg-primary)', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem', fontSize: '0.8rem', maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                    {run.output}
                                  </pre>
                                </details>
                              ) : <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{'\u2014'}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Pagination */}
                  {taskRunsTotal > 20 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                      <button className="btn btn-secondary" style={{ padding: '4px 12px' }}
                        disabled={taskRunsPage <= 1} onClick={() => fetchTaskRuns(selectedTaskId!, taskRunsPage - 1)}>Prev</button>
                      <span style={{ color: 'var(--text-secondary)', alignSelf: 'center', fontSize: '0.85rem' }}>
                        Page {taskRunsPage} of {Math.ceil(taskRunsTotal / 20)}
                      </span>
                      <button className="btn btn-secondary" style={{ padding: '4px 12px' }}
                        disabled={taskRunsPage >= Math.ceil(taskRunsTotal / 20)} onClick={() => fetchTaskRuns(selectedTaskId!, taskRunsPage + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Recent Device Task Activity */}
          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Recent Task Activity</h4>
            {recentDeviceRuns.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No task activity yet</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Task</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Timestamp</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Status</th>
                    <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Output</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDeviceRuns.map((run: any) => {
                    const statusColor = run.status === 'success' ? 'var(--success)' : run.status === 'failed' ? 'var(--danger)' : 'var(--warning)';
                    return (
                      <tr key={run.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td style={{ padding: '0.5rem', fontWeight: 500, fontSize: '0.85rem' }}>{run.task?.name || 'Deleted Task'}</td>
                        <td style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{new Date(run.startedAt).toLocaleString()}</td>
                        <td style={{ padding: '0.5rem' }}>
                          <span style={{ background: statusColor, color: run.status === 'missed' ? '#000' : '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                            {run.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {run.output ? run.output.substring(0, 100) : '\u2014'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Device Info */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', fontWeight: 600 }}>
          <Settings size={20} color="var(--accent-primary)" /> Device Info
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: 500 }}>Nickname</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                defaultValue={device.nickname || ''}
                placeholder={device.hostname}
                id="settings-nickname"
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.875rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: 500 }}>Notes</label>
            <textarea
              defaultValue={device.notes || ''}
              placeholder="Add notes about this device (location, purpose, etc.)"
              id="settings-notes"
              rows={3}
              style={{ width: '100%', padding: '0.5rem 0.75rem', fontSize: '0.875rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ alignSelf: 'flex-start', background: 'var(--accent-primary)', color: '#000' }}
            onClick={async () => {
              const nickname = (document.getElementById('settings-nickname') as HTMLInputElement).value;
              const notes = (document.getElementById('settings-notes') as HTMLTextAreaElement).value;
              await fetch(`${API_BASE}/api/devices/${id}/settings`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nickname, notes }),
              });
              fetchData();
            }}
          >
            Save
          </button>
        </div>
      </div>

      {/* Tags */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', fontWeight: 600 }}>
          <Database size={20} color="#a855f7" /> Device Tags
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {(() => {
            const tags: string[] = (() => { try { return JSON.parse(device.tags || '[]'); } catch { return []; } })();
            const allTags = ['server', 'desktop', 'laptop', 'mobile'];
            return allTags.map(tag => {
              const active = tags.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={async () => {
                    const newTags = active ? tags.filter((t: string) => t !== tag) : [...tags, tag];
                    await fetch(`${API_BASE}/api/devices/${id}/settings`, {
                      method: 'PUT', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ tags: newTags }),
                    });
                    fetchData();
                  }}
                  style={{
                    padding: '0.4rem 1rem', borderRadius: '20px', border: active ? '2px solid #a855f7' : '1px solid var(--border)',
                    background: active ? 'rgba(168, 85, 247, 0.15)' : 'transparent',
                    color: active ? '#a855f7' : 'var(--text-secondary)',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s',
                  }}
                >
                  {tag}
                </button>
              );
            });
          })()}
        </div>
      </div>

      {/* Auto-Update */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
          <Download size={20} color="#0ea5e9" /> Agent Auto-Update
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 500 }}>Automatic Updates</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {device.autoUpdate !== false ? 'Agent will check for and install updates automatically.' : 'Agent is pinned to its current version.'}
            </div>
          </div>
          <button
            className="btn btn-secondary"
            style={{
              background: device.autoUpdate !== false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              color: device.autoUpdate !== false ? 'var(--success)' : 'var(--danger)',
              border: `1px solid ${device.autoUpdate !== false ? 'var(--success)' : 'var(--danger)'}`,
            }}
            onClick={async () => {
              await fetch(`${API_BASE}/api/devices/${id}/settings`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ autoUpdate: device.autoUpdate === false }),
              });
              fetchData();
            }}
          >
            {device.autoUpdate !== false ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Silence Notifications */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', fontWeight: 600 }}>
          <BellOff size={20} color="#eab308" /> Silence Notifications
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
          Temporarily silence all email alerts for this device. Alerts will resume automatically when the silence period expires.
        </p>

        {device.silencedUntil && new Date(device.silencedUntil) > new Date() ? (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <BellOff size={20} color="#eab308" />
                <div>
                  <div style={{ fontWeight: 600, color: '#eab308' }}>Alerts silenced</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{new Date(device.silencedUntil).getFullYear() >= 9999 ? 'Indefinitely' : `Until ${new Date(device.silencedUntil).toLocaleString()}`}</div>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                disabled={silenceLoading}
                onClick={async () => {
                  setSilenceLoading(true);
                  try {
                    await fetch(`${API_BASE}/api/devices/${id}/silence`, { method: 'DELETE', credentials: 'include' });
                    fetchData();
                  } finally { setSilenceLoading(false); }
                }}
              >
                <Bell size={14} /> Unsilence Now
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[
              { label: '5 minutes', mins: 5 },
              { label: '15 minutes', mins: 15 },
              { label: '30 minutes', mins: 30 },
              { label: '1 hour', mins: 60 },
              { label: '3 hours', mins: 180 },
              { label: '1 day', mins: 1440 },
            ].map(opt => (
              <button
                key={opt.mins}
                className="btn btn-secondary"
                disabled={silenceLoading}
                style={{ padding: '0.6rem 1.2rem' }}
                onClick={async () => {
                  setSilenceLoading(true);
                  try {
                    const until = new Date(Date.now() + opt.mins * 60000).toISOString();
                    await fetch(`${API_BASE}/api/devices/${id}/silence`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ until }),
                    });
                    fetchData();
                  } finally { setSilenceLoading(false); }
                }}
              >
                <BellOff size={14} /> {opt.label}
              </button>
            ))}
            <button
              className="btn btn-secondary"
              disabled={silenceLoading}
              style={{ padding: '0.6rem 1.2rem' }}
              onClick={async () => {
                setSilenceLoading(true);
                try {
                  const until = new Date('9999-12-31T23:59:59Z').toISOString();
                  await fetch(`${API_BASE}/api/devices/${id}/silence`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ until }),
                  });
                  fetchData();
                } finally { setSilenceLoading(false); }
              }}
            >
              <BellOff size={14} /> Indefinitely
            </button>
          </div>
        )}

        {!(device.silencedUntil && new Date(device.silencedUntil) > new Date()) && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
            <h4 style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Custom Duration</h4>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="datetime-local"
                value={customSilenceDate}
                onChange={e => setCustomSilenceDate(e.target.value)}
                style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)' }}
              />
              <button
                className="btn btn-secondary"
                disabled={silenceLoading || !customSilenceDate}
                onClick={async () => {
                  if (!customSilenceDate) return;
                  setSilenceLoading(true);
                  try {
                    const until = new Date(customSilenceDate).toISOString();
                    await fetch(`${API_BASE}/api/devices/${id}/silence`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ until }),
                    });
                    setCustomSilenceDate('');
                    fetchData();
                  } finally { setSilenceLoading(false); }
                }}
              >
                <BellOff size={14} /> Silence Until
              </button>
            </div>
          </div>
        )}
      </div>

      </div>
      )}

    </div>
  );
}
